import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Users, Settings, Shuffle, Download, Upload, RotateCcw, Plus, X, Link, Ban, AlertTriangle, BookCopy, LayoutGrid, Users2, Check, MapPin, ArrowDownAZ, Grid3x3, Clipboard, Printer } from 'lucide-react';


const Dropdown = ({ buttonText, icon, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const toggleDropdown = () => setIsOpen(!isOpen);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const childrenWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            const originalOnClick = child.props.onClick;
            return React.cloneElement(child, {
                onClick: (e) => {
                    if (originalOnClick) {
                        originalOnClick(e);
                    }
                    if (child.type !== 'label') {
                        setIsOpen(false);
                    }
                },
            });
        }
        return child;
    });
    return (
        <div className="relative" ref={dropdownRef}>
             <button onClick={toggleDropdown} className="flex items-center gap-2 px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm transition-colors">
                 {icon} {buttonText}
                 <svg className={`w-4 h-4 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
             </button>
             {isOpen && (
                 <div className="absolute right-0 z-[70] mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                     <div className="py-1" role="menu">{childrenWithProps}</div>
                 </div>
             )}
        </div>
    );
};

// KORRIGERT RosterManager
const RosterManager = ({ rosters, setRosters, currentStudents, currentRelationships, onRosterLoad, onClose, currentGroups }) => {
    const [selectedRoster, setSelectedRoster] = useState(null);
    const handleSaveCurrent = () => {
        const rosterName = prompt("Hva vil du kalle denne elevlisten?");
        if (rosterName && rosterName.trim()) {
            const newRosterId = 'roster_' + Date.now();
            const newRoster = {
                id: newRosterId,
                name: rosterName.trim(),
                students: currentStudents,
                studentRelationships: currentRelationships,
                groups: currentGroups // VIKTIG: Lagrer gruppenavnene
            };
            setRosters(prev => ({ ...prev, [newRosterId]: newRoster }));
        }
    };
    const handleLoadSelected = () => {
        if (!selectedRoster) {
            alert("Vennligst velg en liste fra biblioteket først.");
            return;
        }
        if (window.confirm(`Er du sikker på at du vil erstatte elevene i klasserommet med listen "${rosters[selectedRoster].name}"?`)) {
            onRosterLoad(rosters[selectedRoster]);
            onClose();
        }
    };
    const handleDeleteSelected = () => {
        if (!selectedRoster) {
            alert("Vennligst velg en liste du vil slette.");
            return;
        }
        if (window.confirm(`Er du sikker på at du vil slette listen "${rosters[selectedRoster].name}" permanent?`)) {
            setRosters(prev => {
                const newRosters = { ...prev };
                delete newRosters[selectedRoster];
                return newRosters;
            });
            setSelectedRoster(null);
        }
    };
    const handleRenameSelected = () => {
        if (!selectedRoster) {
            alert("Vennligst velg en liste du vil gi nytt navn.");
            return;
        }
        const newName = prompt(`Nytt navn for "${rosters[selectedRoster].name}":`);
        if (newName && newName.trim()) {
            setRosters(prev => ({
                ...prev,
                [selectedRoster]: { ...prev[selectedRoster], name: newName.trim() }
            }));
        }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[100]">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold">Elevliste-bibliotek</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </div>
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-medium mb-2">Bibliotek</h3>
                        <div className="border rounded-md h-64 overflow-y-auto bg-gray-50">
                            {Object.keys(rosters).length === 0 ? (
                                <p className="p-4 text-sm text-gray-500 italic">Biblioteket er tomt.</p>
                            ) : (
                                Object.values(rosters).map(roster => (
                                    <div key={roster.id} onClick={() => setSelectedRoster(roster.id)} className={`p-2 cursor-pointer text-sm ${selectedRoster === roster.id ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}>
                                        {roster.name} ({roster.students.length} elever)
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="flex gap-2 mt-2">
                            <button onClick={handleRenameSelected} disabled={!selectedRoster} className="flex-1 px-3 py-1 bg-yellow-500 text-white rounded text-sm disabled:opacity-50">Gi nytt navn</button>
                            <button onClick={handleDeleteSelected} disabled={!selectedRoster} className="flex-1 px-3 py-1 bg-red-500 text-white rounded text-sm disabled:opacity-50">Slett</button>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <button onClick={handleSaveCurrent} className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Lagre nåværende elever som ny liste</button>
                        <button onClick={handleLoadSelected} disabled={!selectedRoster} className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">Last inn valgt liste i klasserom</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PlacementManager = ({ student, onSave, onClose }) => {
    const [selections, setSelections] = useState(
    Array.isArray(student.placement) ? student.placement : (student.placement ? [student.placement] : [])
);

    const positions = [
        { id: 'topLeft', label: 'Foran Venstre' }, { id: 'topCenter', label: 'Foran Midten' }, { id: 'topRight', label: 'Foran Høyre' },
        { id: 'midLeft', label: 'Midten Venstre' }, { id: 'midCenter', label: 'Midten Sentrum' }, { id: 'midRight', label: 'Midten Høyre' },
        { id: 'bottomLeft', label: 'Bak Venstre' }, { id: 'bottomCenter', label: 'Bak Midten' }, { id: 'bottomRight', label: 'Bak Høyre' },
    ];

    const handleCellClick = (positionId) => {
        const existingIndex = selections.findIndex(s => s.position === positionId);

        if (existingIndex === -1) {
            setSelections([...selections, { position: positionId, strength: 'should' }]);
        } else {
            const existingSelection = selections[existingIndex];
            if (existingSelection.strength === 'should') {
                const newSelections = [...selections];
                newSelections[existingIndex] = { ...existingSelection, strength: 'must' };
                setSelections(newSelections);
            } else {
                setSelections(selections.filter(s => s.position !== positionId));
            }
        }
    };

    const getButtonClass = (positionId) => {
        const selection = selections.find(s => s.position === positionId);
        if (selection) {
            if (selection.strength === 'should') return 'bg-yellow-400 text-yellow-900 ring-2 ring-yellow-500';
            if (selection.strength === 'must') return 'bg-red-500 text-white ring-2 ring-red-700';
        }
        return 'bg-gray-200 hover:bg-gray-300';
    };
    
    const getButtonText = (positionId) => {
        const selection = selections.find(s => s.position === positionId);
        if (selection) {
            return selection.strength === 'should' ? 'BØR' : 'MÅ';
        }
        return '';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[110]">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Plassering for {student.name}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </div>
                
                <p className="text-sm text-center text-gray-600 mb-4">
                    Velg en eller flere foretrukne plasseringer. Klikk for <strong>Bør</strong>, igjen for <strong>Må</strong>.
                </p>
                
                <div className="grid grid-cols-3 gap-2 aspect-square">
                    {positions.map(pos => (
                        <button
                            key={pos.id}
                            onClick={() => handleCellClick(pos.id)}
                            title={pos.label}
                            className={`flex items-center justify-center rounded-lg transition-all duration-150 text-xs font-semibold ${getButtonClass(pos.id)}`}
                        >
                            {getButtonText(pos.id)}
                        </button>
                    ))}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm hover:bg-gray-300">Avbryt</button>
                    <button onClick={() => onSave(student.id, selections)} className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">Lagre</button>
                </div>
            </div>
        </div>
    );
};

const RelationshipEditor = ({ studentInFocus, allStudents, relationships, onUpdate, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const getStudentName = (id) => allStudents.find(s => s.id === id)?.name || 'Ukjent';

    const getRelatedStudents = (type) => {
        return relationships[type]
            .filter(pair => pair.includes(studentInFocus.id))
            .map(pair => (pair[0] === studentInFocus.id ? pair[1] : pair[0]));
    };

   const availableStudents = allStudents
    .filter(s => s.id !== studentInFocus.id && s.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="bg-white border rounded-lg p-4 transition-all duration-300">
            <div className="flex justify-between items-center mb-4 pb-4 border-b">
                <h3 className="text-xl font-semibold">
                    Relasjoner for <span className="text-blue-600">{studentInFocus.name}</span>
                </h3>
                <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm hover:bg-gray-300">Lukk</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h4 className="font-medium text-gray-800 mb-2">Legg til relasjon til:</h4>
                    <input
                        type="text"
                        placeholder="Søk etter elev..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 text-sm"
                    />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-96 overflow-y-auto pr-2 border rounded-lg p-2 bg-gray-50">
                        {availableStudents.map(student => (
                            <div key={student.id} className="bg-white p-2 rounded shadow-sm text-sm flex justify-between items-center">
                                <span>{student.name}</span>
                                <div className="flex gap-1">
                                    <button onClick={() => onUpdate('mustSitTogether', studentInFocus.id, student.id)} title="Må sitte med" className="p-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200"><Link size={14}/></button>
                                    <button onClick={() => onUpdate('cannotSitAdjacent', studentInFocus.id, student.id)} title="Kan ikke sitte ved siden av" className="p-1 rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200"><Ban size={14}/></button>
                                    <button onClick={() => onUpdate('cannotSitNearby', studentInFocus.id, student.id)} title="Kan ikke sitte nær" className="p-1 rounded-full bg-red-100 text-red-700 hover:bg-red-200"><AlertTriangle size={14}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h4 className="font-medium text-gray-800 mb-2">Eksisterende relasjoner:</h4>
                    <div className="space-y-3">
                        <div className="p-2 border-l-4 border-green-400 bg-green-50 rounded-r-lg">
                            <h5 className="font-semibold text-green-800 text-sm mb-1">Må sitte med:</h5>
                            {getRelatedStudents('mustSitTogether').map(id => (
                                <div key={id} className="flex justify-between items-center text-sm">
                                    <span>{getStudentName(id)}</span>
                                    <button onClick={() => onUpdate('mustSitTogether', studentInFocus.id, id, true)} className="text-gray-400 hover:text-red-500"><X size={14}/></button>
                                </div>
                            ))}
                        </div>
                         <div className="p-2 border-l-4 border-orange-400 bg-orange-50 rounded-r-lg">
                            <h5 className="font-semibold text-orange-800 text-sm mb-1">Kan ikke sitte ved siden av:</h5>
                            {getRelatedStudents('cannotSitAdjacent').map(id => (
                                <div key={id} className="flex justify-between items-center text-sm">
                                    <span>{getStudentName(id)}</span>
                                    <button onClick={() => onUpdate('cannotSitAdjacent', studentInFocus.id, id, true)} className="text-gray-400 hover:text-red-500"><X size={14}/></button>
                                </div>
                            ))}
                        </div>
                        <div className="p-2 border-l-4 border-red-400 bg-red-50 rounded-r-lg">
                            <h5 className="font-semibold text-red-800 text-sm mb-1">Kan ikke sitte nær:</h5>
                             {getRelatedStudents('cannotSitNearby').map(id => (
                                <div key={id} className="flex justify-between items-center text-sm">
                                    <span>{getStudentName(id)}</span>
                                    <button onClick={() => onUpdate('cannotSitNearby', studentInFocus.id, id, true)} className="text-gray-400 hover:text-red-500"><X size={14}/></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


const ClassroomDesigner = () => {
    // State for responsive canvas
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    // Hoved-state for applikasjonen
    const [currentView, setCurrentView] = useState('setup');
    const [currentClassroom, setCurrentClassroom] = useState(null);
    const [classrooms, setClassrooms] = useState({});
    const [rosters, setRosters] = useState({});

    // State for det AKTIVE klasserommet (disse blir lagret inn i `classrooms`-objektet)
    const [students, setStudents] = useState([]);
    const [desks, setDesks] = useState([]);
    const [podium, setPodium] = useState(null);
    const [seatingArrangement, setSeatingArrangement] = useState({});
    const [studentRelationships, setStudentRelationships] = useState({ mustSitTogether: [], canSitTogether: [], cannotSitAdjacent: [], cannotSitNearby: [] });
    const [groups, setGroups] = useState({ A: 'Gruppe A', B: 'Gruppe B' });
    
    // State for innstillinger (disse blir også lagret)
    const [seatingRules, setSeatingRules] = useState({ alternateGender: false, separateTroublesome: true, prioritizePlacement: false });
    const [colorSettings, setColorSettings] = useState({
        gender: { gutt: '#fde68a', jente: '#d8b4fe', annet: '#d1d5db' },
        group: { A: '#a7f3d0', B: '#fed7aa' },
        frame: '#6b7280',
        podium: '#7c3aed',
        textColor: '#1f2937'
    });
    const [textStyle, setTextStyle] = useState({ bold: false, italic: false });
    const [chartOptions, setChartOptions] = useState({ showGenderColors: true, showGroupColors: false });
    
    // UI/diverse state
    const [newStudent, setNewStudent] = useState({ name: '', gender: 'gutt' });
    const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);
    const canvasRef = useRef(null);
    const seatingCanvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [showPodiumPlacement, setShowPodiumPlacement] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [isGroupingMode, setIsGroupingMode] = useState(false);
    const [groupFilter, setGroupFilter] = useState('all');
    const [selectedForSwap, setSelectedForSwap] = useState(null);
    const [editingPlacementFor, setEditingPlacementFor] = useState(null);
    const [editingRelationshipsFor, setEditingRelationshipsFor] = useState(null);

     const [generatedGroups, setGeneratedGroups] = useState({});
    const [groupGenSettings, setGroupGenSettings] = useState({
        method: 'byCount',
        count: 4,
        size: 4,
        balanceGender: true,
        generateWithinSubgroups: false
    });

    // Konstanter
    const gridSize = 53;
    const canvasSize = useMemo(() => ({ width: 800, height: 533 }), []);

    // ===== START: LØSNING FOR LAGRING & INNLASTING =====

    const loadClassroomData = useCallback((classroom) => {
        if (!classroom) return;
        setDesks(classroom.desks || []);
        setStudents((classroom.students || []).map(s => ({
            ...s,
            group: s.group || null,
            placement: Array.isArray(s.placement) ? s.placement : []
        })));
        setGroups(classroom.groups || { A: 'Gruppe A', B: 'Gruppe B' });
        setSeatingArrangement(classroom.seatingArrangement || {});
        setPodium(classroom.podium || null);
        const loadedRel = classroom.studentRelationships || {};
        setStudentRelationships({
            mustSitTogether: Array.isArray(loadedRel.mustSitTogether) ? loadedRel.mustSitTogether : [],
            canSitTogether: Array.isArray(loadedRel.canSitTogether) ? loadedRel.canSitTogether : [],
            cannotSitAdjacent: Array.isArray(loadedRel.cannotSitAdjacent) ? loadedRel.cannotSitAdjacent : [],
            cannotSitNearby: Array.isArray(loadedRel.cannotSitNearby) ? loadedRel.cannotSitNearby : [],
        });
        setSeatingRules(classroom.seatingRules || { alternateGender: false, separateTroublesome: true, prioritizePlacement: false });
        setColorSettings(classroom.colorSettings || { gender: { gutt: '#fde68a', jente: '#d8b4fe', annet: '#d1d5db' }, group: { A: '#a7f3d0', B: '#fed7aa' }, frame: '#6b7280', podium: '#7c3aed', textColor: '#1f2937' });
        setTextStyle(classroom.textStyle || { bold: false, italic: false });
        setChartOptions(classroom.chartOptions || { showGenderColors: true, showGroupColors: false });
        setGeneratedGroups(classroom.lastGeneratedGroups || {});
    }, []);

    useEffect(() => {
        const savedState = localStorage.getItem('classroomDesigner_appState');
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                if (parsed.classrooms && Object.keys(parsed.classrooms).length > 0) {
                    setClassrooms(parsed.classrooms);
                    setRosters(parsed.rosters || {});
                    const firstId = Object.keys(parsed.classrooms)[0];
                    setCurrentClassroom(firstId);
                    loadClassroomData(parsed.classrooms[firstId]);
                } else {
                    const defaultId = 'classroom_' + Date.now();
                    const defaultClassroom = { id: defaultId, name: 'Mitt Klasserom', desks: [], students: [], seatingArrangement: {}, podium: null, groups: { A: 'Gruppe A', B: 'Gruppe B' }, studentRelationships: { mustSitTogether: [], canSitTogether: [], cannotSitAdjacent: [], cannotSitNearby: [] }, seatingRules: { alternateGender: false, separateTroublesome: true, prioritizePlacement: false }, colorSettings, textStyle, chartOptions };
                    setClassrooms({ [defaultId]: defaultClassroom });
                    setCurrentClassroom(defaultId);
                    loadClassroomData(defaultClassroom);
                }
            } catch (error) {
                console.error("Failed to parse saved state:", error);
            }
        } else {
            const defaultId = 'classroom_' + Date.now();
            const defaultClassroom = { id: defaultId, name: 'Mitt Klasserom', desks: [], students: [], seatingArrangement: {}, podium: null, groups: { A: 'Gruppe A', B: 'Gruppe B' }, studentRelationships: { mustSitTogether: [], canSitTogether: [], cannotSitAdjacent: [], cannotSitNearby: [] }, seatingRules: { alternateGender: false, separateTroublesome: true, prioritizePlacement: false }, colorSettings, textStyle, chartOptions };
            setClassrooms({ [defaultId]: defaultClassroom });
            setCurrentClassroom(defaultId);
            loadClassroomData(defaultClassroom);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

   useEffect(() => {
        if (!currentClassroom) return;

        setClassrooms(prev => {
            if (!prev[currentClassroom]) return prev;

            const updatedClassroom = {
                ...prev[currentClassroom],
                students,
                desks,
                podium,
                seatingArrangement,
                studentRelationships,
                groups,
                seatingRules,
                colorSettings,
                textStyle,
                chartOptions,
                lastGeneratedGroups: generatedGroups
            };

            return {
                ...prev,
                [currentClassroom]: updatedClassroom
            };
        });
    // KORREKT PLASSERING: Listen er inni den siste parentesen til useEffect
    }, [students, desks, podium, seatingArrangement, studentRelationships, groups, seatingRules, colorSettings, textStyle, chartOptions, currentClassroom, generatedGroups]);

    useEffect(() => {
        if (Object.keys(classrooms).length > 0) {
            const appState = {
                classrooms,
                rosters
            };
            localStorage.setItem('classroomDesigner_appState', JSON.stringify(appState));
        }
    }, [classrooms, rosters]);
    
    // ===== SLUTT: LØSNING FOR LAGRING & INNLASTING =====


    // ===== START: LØSNING FOR FARGEKODING AV IKONER =====

    const getRelationshipStatusColor = useCallback((studentId) => {
        const rels = studentRelationships;
        const hasRelationship = (type) => rels[type]?.some(pair => pair.includes(studentId));

        if (hasRelationship('cannotSitNearby')) return 'text-red-500 hover:text-red-600';
        if (hasRelationship('cannotSitAdjacent')) return 'text-orange-500 hover:text-orange-600';
        if (hasRelationship('mustSitTogether')) return 'text-green-500 hover:text-green-600';
        
        return 'text-gray-400 hover:text-blue-500'; // Standardfarge
    }, [studentRelationships]);

    const getPlacementStatusColor = useCallback((student) => {
        if (!Array.isArray(student.placement) || student.placement.length === 0) {
            return 'text-gray-400 hover:text-blue-500'; // Standardfarge
        }
        
        const hasMust = student.placement.some(p => p.strength === 'must');
        const hasShould = student.placement.some(p => p.strength === 'should');

        if (hasMust) return 'text-red-500 hover:text-red-600';
        if (hasShould) return 'text-orange-500 hover:text-orange-600';

        return 'text-gray-400 hover:text-blue-500'; // Standardfarge hvis noe er rart
    }, []);

// Hjelpefunksjon som inneholder kjernelogikken
    const createOptimalGroups = (studentsToGroup, settings) => {
        if (studentsToGroup.length === 0) return [];

        let numGroups;
        if (settings.method === 'byCount') {
            numGroups = Math.max(1, settings.count);
        } else { // bySize
            const size = Math.max(2, settings.size);
            numGroups = Math.ceil(studentsToGroup.length / size);
        }

        if (studentsToGroup.length < numGroups) {
            return [];
        }

        let bestGrouping = [];
        let bestScore = -Infinity;

        const iterations = 200;
        for (let i = 0; i < iterations; i++) {
            let availableStudents = [...studentsToGroup].sort(() => 0.5 - Math.random());
            let currentGrouping = Array.from({ length: numGroups }, () => []);
            let currentScore = 0;

            studentRelationships.mustSitTogether.forEach(pair => {
                const s1 = availableStudents.find(s => s.id === pair[0]);
                const s2 = availableStudents.find(s => s.id === pair[1]);
                if (s1 && s2) {
                    const targetGroupIndex = currentGrouping.findIndex(g => g.length <= (studentsToGroup.length / numGroups) - 2);
                    if (targetGroupIndex !== -1) {
                         currentGrouping[targetGroupIndex].push(s1, s2);
                         availableStudents = availableStudents.filter(s => s.id !== s1.id && s.id !== s2.id);
                    }
                }
            });

            availableStudents.forEach(student => {
                let bestGroupIndex = -1;
                let bestGroupScore = -Infinity;

                for (let j = 0; j < numGroups; j++) {
                    let tempScore = Math.random();
                    const group = currentGrouping[j];
                    
                    let hasConflict = false;
                    for (const member of group) {
                        const relPair = [student.id, member.id].sort((a,b)=>a-b);
                        const isConflict = studentRelationships.cannotSitAdjacent.some(p => p[0] === relPair[0] && p[1] === relPair[1]) || 
                                         studentRelationships.cannotSitNearby.some(p => p[0] === relPair[0] && p[1] === relPair[1]);
                        if (isConflict) {
                            hasConflict = true;
                            break;
                        }
                    }
                    if (hasConflict) tempScore -= 1000;

                    if (settings.balanceGender && group.length > 0) {
                        const boys = group.filter(s => s.gender === 'gutt').length + (student.gender === 'gutt' ? 1 : 0);
                        const girls = group.filter(s => s.gender === 'jente').length + (student.gender === 'jente' ? 1 : 0);
                        tempScore += 10 - Math.abs(boys - girls) * 5;
                    }
                    
                    const baseSize = Math.floor(studentsToGroup.length / numGroups);
                    if(group.length >= baseSize + 1) tempScore -= 50;

                    if (tempScore > bestGroupScore) {
                        bestGroupScore = tempScore;
                        bestGroupIndex = j;
                    }
                }
                
                const targetIndex = bestGroupIndex !== -1 ? bestGroupIndex : currentGrouping.findIndex(g => g.length < Math.ceil(studentsToGroup.length / numGroups));
                if (targetIndex !== -1) {
                    currentGrouping[targetIndex].push(student);
                }
            });
            
            currentGrouping.forEach(group => {
                if (settings.balanceGender) {
                    const boys = group.filter(s => s.gender === 'gutt').length;
                    const girls = group.filter(s => s.gender === 'jente').length;
                    currentScore += 20 - Math.abs(boys - girls) * 10;
                }
            });

            if (currentScore > bestScore) {
                bestScore = currentScore;
                bestGrouping = currentGrouping;
            }
        }
        return bestGrouping;
    };
    
    // Hovedfunksjon som kaller hjelpefunksjonen
    const generateGroups = () => {
        let result = {};
        
        if (groupGenSettings.generateWithinSubgroups) {
            const studentsA = students.filter(s => s.group === 'A');
            const studentsB = students.filter(s => s.group === 'B');
            
            if (studentsA.length === 0 && studentsB.length === 0) {
                alert("Det finnes ingen elever i delingsgruppene A eller B.");
                return;
            }

            const groupsA = createOptimalGroups(studentsA, groupGenSettings);
            const groupsB = createOptimalGroups(studentsB, groupGenSettings);
            
            if(studentsA.length > 0 && groupsA.length === 0) alert(`Kunne ikke lage grupper for ${groups.A} med de valgte innstillingene (for mange grupper?).`);
            if(studentsB.length > 0 && groupsB.length === 0) alert(`Kunne ikke lage grupper for ${groups.B} med de valgte innstillingene (for mange grupper?).`);

            result = { A: groupsA, B: groupsB };
        } else {
            const allGroups = createOptimalGroups(students, groupGenSettings);
            if(students.length > 0 && allGroups.length === 0) {
                 alert("Kunne ikke lage grupper med de valgte innstillingene (for mange grupper?).");
            }
            result = { all: allGroups };
        }
        setGeneratedGroups(result);
    };

    // Funksjoner for eksport, kopiering og utskrift
   const getGroupsAsText = () => {
        let text = '';
        const classroomName = classrooms[currentClassroom]?.name || 'Mine grupper';
        text += `${classroomName}\n${new Date().toLocaleDateString('nb-NO')}\n\n`;
    
        if (generatedGroups.all) {
            text += generatedGroups.all.map((group, index) => {
                const studentNames = group.map(student => student.name).join('\n');
                return `Gruppe ${index + 1}:\n${studentNames}`;
            }).join('\n\n');
        } else {
            const textA = generatedGroups.A?.length > 0
                ? `--- ${groups.A} ---\n` + generatedGroups.A.map((group, index) => { // Bruker dynamisk navn
                    const studentNames = group.map(student => student.name).join('\n');
                    return `Gruppe ${index + 1}:\n${studentNames}`;
                }).join('\n\n')
                : '';
    
            const textB = generatedGroups.B?.length > 0
                ? `--- ${groups.B} ---\n` + generatedGroups.B.map((group, index) => { // Bruker dynamisk navn
                    const studentNames = group.map(student => student.name).join('\n');
                    return `Gruppe ${index + 1}:\n${studentNames}`;
                }).join('\n\n')
                : '';
            
            text += [textA, textB].filter(Boolean).join('\n\n\n');
        }
        return text;
    };
    
    const copyGroupsToClipboard = () => {
        const text = getGroupsAsText();
        if(!text.trim()) {
            alert("Ingenting å kopiere.");
            return;
        }
        navigator.clipboard.writeText(text).then(() => {
            alert('Gruppene er kopiert til utklippstavlen!');
        }).catch(() => alert('Kopiering feilet.'));
    };

    const exportGroupsAsTxt = () => {
        const text = getGroupsAsText();
        if (!text.trim()) {
            alert("Ingenting å eksportere.");
            return;
        }
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `grupper_${classrooms[currentClassroom]?.name.replace(/ /g, '_') || 'export'}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ===== SLUTT: LØSNING FOR FARGEKODING AV IKONER =====


    useEffect(() => {
        const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const handleSavePlacement = (studentId, newPlacement) => {
        setStudents(prevStudents =>
            prevStudents.map(s =>
                s.id === studentId ? { ...s, placement: newPlacement } : s
            )
        );
        setEditingPlacementFor(null);
    };

    const handleUpdateRelationship = (type, student1Id, student2Id, isRemoval = false) => {
        const pair = [student1Id, student2Id].sort((a, b) => a - b);
    
        setStudentRelationships(prev => {
            const newRels = JSON.parse(JSON.stringify(prev));
    
            if (isRemoval) {
                newRels[type] = newRels[type].filter(p => !(p[0] === pair[0] && p[1] === pair[1]));
            } else {
                Object.keys(newRels).forEach(key => {
                    newRels[key] = newRels[key].filter(p => !(p[0] === pair[0] && p[1] === pair[1]));
                });
                newRels[type].push(pair);
            }
            return newRels;
        });
    };
    
    const getStudentColors = useCallback((student) => {
        if (!student) return { primary: colorSettings.gender.annet };
        if (chartOptions.showGroupColors && student.group && colorSettings.group[student.group]) {
            return { primary: colorSettings.group[student.group] };
        }
        if (chartOptions.showGenderColors && colorSettings.gender[student.gender]) {
            return { primary: colorSettings.gender[student.gender] };
        }
        return { primary: colorSettings.gender.annet };
    }, [chartOptions, colorSettings]);
    
    const switchClassroom = (classroomId) => {
        if (classroomId === currentClassroom) return;
        setCurrentClassroom(classroomId);
        loadClassroomData(classrooms[classroomId]);
    };

    const createNewClassroom = () => {
        const newId = 'classroom_' + Date.now();
        const newClassroom = {
            id: newId,
            name: `Klasserom ${Object.keys(classrooms).length + 1}`,
            desks: [], students: [], seatingArrangement: {}, podium: null,
            groups: { A: 'Gruppe A', B: 'Gruppe B' },
            studentRelationships: { mustSitTogether: [], canSitTogether: [], cannotSitAdjacent: [], cannotSitNearby: [] },
            seatingRules: { alternateGender: false, separateTroublesome: true, prioritizePlacement: false },
            colorSettings, textStyle, chartOptions
        };
        setClassrooms(prev => ({...prev, [newId]: newClassroom}));
        switchClassroom(newId);
    };
    
    const renameClassroom = (newName) => {
        if (!currentClassroom || !newName.trim()) return;
        setClassrooms(prev => ({
            ...prev,
            [currentClassroom]: { ...prev[currentClassroom], name: newName.trim() }
        }));
    };

    const deleteClassroom = (classroomId) => {
        if (Object.keys(classrooms).length <= 1) {
            alert('Du kan ikke slette det siste klasserommet.');
            return;
        }
        if (!window.confirm(`Er du sikker på at du vil slette "${classrooms[classroomId].name}"?`)) return;
        
        const newClassrooms = { ...classrooms };
        delete newClassrooms[classroomId];
        
        let newCurrentId = currentClassroom;
        if (classroomId === currentClassroom) {
            newCurrentId = Object.keys(newClassrooms)[0];
        }
        
        setClassrooms(newClassrooms);
        switchClassroom(newCurrentId);
    };

    const addStudent = () => {
        if (newStudent.name.trim()) {
            const student = {
                id: Date.now(),
                name: newStudent.name.trim(),
                gender: newStudent.gender,
                group: null,
                placement: []
            };
            setStudents(prev => [...prev, student]);
            setNewStudent({ name: '', gender: 'gutt' });
        }
    };

    const removeStudent = (id) => {
        setStudents(students.filter(s => s.id !== id));
        setStudentRelationships(prev => ({
            mustSitTogether: prev.mustSitTogether.filter(pair => !pair.includes(id)),
            canSitTogether: prev.canSitTogether.filter(pair => !pair.includes(id)),
            cannotSitAdjacent: prev.cannotSitAdjacent.filter(pair => !pair.includes(id)),
            cannotSitNearby: prev.cannotSitNearby.filter(pair => !pair.includes(id))
        }));
    };

    const updateStudentGender = (studentId, newGender) => {
        setStudents(students.map(student =>
            student.id === studentId ? { ...student, gender: newGender } : student
        ));
    };

    // KORRIGERT handleRosterLoad
    const handleRosterLoad = (roster) => {
        setStudents((roster.students || []).map(s => ({ 
            ...s, 
            group: s.group || null, 
            placement: Array.isArray(s.placement) ? s.placement : []
        })));
        // VIKTIG: Laster inn gruppenavnene fra listen
        setGroups(roster.groups || { A: 'Gruppe A', B: 'Gruppe B' });
        setStudentRelationships(roster.studentRelationships || { mustSitTogether: [], canSitTogether: [], cannotSitAdjacent: [], cannotSitNearby: [] });
    };

    const exportClassrooms = () => {
        const finalStateToExport = {
            ...classrooms,
            [currentClassroom]: {
                ...classrooms[currentClassroom],
                students, desks, podium, seatingArrangement, studentRelationships, groups,
                seatingRules, colorSettings, textStyle, chartOptions
            }
        };

        const data = { classrooms: finalStateToExport, rosters, exportDate: new Date().toISOString(), version: '1.4' };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `klasserom_og_lister_eksport.json`;
        a.click();
        URL.revokeObjectURL(url);
    };
    
    const importClassrooms = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                if (data.classrooms) {
                    if (window.confirm("Dette vil overskrive alle nåværende klasserom og elevlister. Er du sikker?")) {
                        setClassrooms(data.classrooms);
                        setRosters(data.rosters || {});
                        const firstId = Object.keys(data.classrooms)[0];
                        if (firstId) {
                            switchClassroom(firstId);
                        }
                        alert(`Importerte ${Object.keys(data.classrooms).length} klasserom og ${Object.keys(data.rosters || {}).length} elevlister.`);
                    }
                }
            } catch (error) { alert('Feil ved import av fil.'); }
        };
        reader.readAsText(file);
        event.target.value = null;
    };


    const clearDesks = () => {
        setDesks([]);
        setSeatingArrangement({});
        setPodium(null);
        setShowPodiumPlacement(false);
    };

    const exportStudentRoster = () => {
        if (students.length === 0) { alert('Ingen elever å eksportere.'); return; }
        const rosterData = { students, studentRelationships, groups };
        const blob = new Blob([JSON.stringify(rosterData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `elevgruppe_${classrooms[currentClassroom]?.name || 'gruppe'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importStudentRoster = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                if (data.students && data.studentRelationships) {
                    if (window.confirm("Vil du erstatte nåværende elevliste og alle relasjoner med data fra denne filen?")) {
                        setStudents(data.students.map(s => ({...s, group: s.group || null})));
                        setGroups(data.groups || { A: 'Gruppe A', B: 'Gruppe B' });
                        setStudentRelationships(data.studentRelationships);
                        alert(`Importerte ${data.students.length} elever og deres relasjoner!`);

                        const rosterName = prompt("Hva vil du kalle denne nye elevlisten i biblioteket ditt? (La stå tomt for å ikke lagre)");
                        if (rosterName && rosterName.trim()) {
                            const newRosterId = 'roster_' + Date.now();
                            const newRoster = {
                                id: newRosterId,
                                name: rosterName.trim(),
                                students: data.students,
                                studentRelationships: data.studentRelationships,
                                groups: data.groups || { A: 'Gruppe A', B: 'Gruppe B' }
                            };
                            setRosters(prev => ({ ...prev, [newRosterId]: newRoster }));
                            alert(`Listen "${rosterName.trim()}" er lagret i biblioteket.`);
                        }
                    }
                } else {
                    alert('Filen ser ikke ut til å være en gyldig elevgruppe-fil.');
                }
            } catch (error) {
                alert('Feil ved import av elevgruppe-fil.');
            }
        };
        reader.readAsText(file);
        event.target.value = null;
    };

    const exportStudentList = () => {
        if (students.length === 0) { alert('Ingen elever å eksportere.'); return; }
        const csv = ['Navn,Kjønn', ...students.map(student => `"${student.name}",${student.gender}`)].join('\n');
        const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `elevliste_${classrooms[currentClassroom]?.name || 'klasserom'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importStudentList = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csv = e.target.result;
                const lines = csv.split('\n').filter(line => line.trim());
                const newStudents = [];
                for (let i = 1; i < lines.length; i++) {
                    const parts = lines[i].split(/[,;]/);
                    const name = parts[0]?.trim().replace(/"/g, '');
                    const gender = parts[1]?.trim().toLowerCase() || 'annet';
                    if (name) {
                        newStudents.push({ id: Date.now() + i, name, gender: ['gutt', 'jente', 'annet'].includes(gender) ? gender : 'annet', group: null });
                    }
                }
                if (newStudents.length > 0) {
                    if (window.confirm(`Fant ${newStudents.length} elever i filen. Vil du legge dem til i den nåværende listen?`)) {
                        const combinedStudents = [...students, ...newStudents];
                        setStudents(combinedStudents);
                        alert(`La til ${newStudents.length} elever!`);
                        
                        const rosterName = prompt("Hva vil du kalle denne nye elevlisten i biblioteket ditt? (La stå tomt for å ikke lagre)");
                        if (rosterName && rosterName.trim()) {
                            const newRosterId = 'roster_' + Date.now();
                            const newRoster = {
                                id: newRosterId,
                                name: rosterName.trim(),
                                students: combinedStudents,
                                studentRelationships: studentRelationships,
                                groups: groups
                            };
                            setRosters(prev => ({ ...prev, [newRosterId]: newRoster }));
                            alert(`Listen "${rosterName.trim()}" er lagret i biblioteket.`);
                        }
                    }
                }
            } catch (error) { alert('Feil ved import av elevliste.'); }
        };
        reader.readAsText(file);
        event.target.value = null;
    };
    
    const exportSeatingChart = () => {
        const canvas = seatingCanvasRef.current;
        if (!canvas) {
            alert("Kunne ikke finne klassekartet. Prøv igjen.");
            return;
        }
        const classroomName = classrooms[currentClassroom]?.name || 'Klassekart';
        
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = canvasSize.width;
        exportCanvas.height = canvasSize.height;
        const ctx = exportCanvas.getContext('2d');
        
        drawCanvasForExport(ctx, classroomName);

        const imageUrl = exportCanvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `klassekart-${classroomName.replace(/ /g, '_')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getCanvasCoords = useCallback((e) => {
        const canvas = canvasRef.current || seatingCanvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const displayGridX = Math.floor((mouseX / rect.width) * (canvasSize.width / gridSize));
        const displayGridY = Math.floor((mouseY / rect.height) * (canvasSize.height / gridSize));
        const canvasX = displayGridX * gridSize;
        const canvasY = displayGridY * gridSize;
        const clampedX = Math.max(0, Math.min(canvasX, canvasSize.width - gridSize));
        const clampedY = Math.max(0, Math.min(canvasY, canvasSize.height - gridSize));
        return { x: clampedX, y: clampedY };
    }, [canvasSize, gridSize]);

    const getDeskType = useCallback((cols, rows) => {
        if (cols === 1 && rows === 1) return 'single';
        if (cols === 2 && rows === 1) return 'pair';
        if (cols === 3 && rows === 1) return 'triple';
        if (cols === 2 && rows === 2) return 'group4';
        if (cols === 3 && rows === 2) return 'group6';
        return `custom_${cols}x${rows}`;
    }, []);

    const getDeskColor = useCallback((type) => {
        switch (type) {
            case 'single': return '#dbeafe';
            case 'pair': return '#dcfce7';
            case 'triple': return '#f3e8ff';
            case 'group4': return '#fef3c7';
            case 'group6': return '#fecaca';
            default: return '#fde68a';
        }
    }, []);

    const getDeskLabel = useCallback((type, capacity) => {
        switch (type) {
            case 'single': return '1';
            case 'pair': return '2';
            case 'triple': return '3';
            case 'group4': return '4';
            case 'group6': return '6';
            default: return capacity.toString();
        }
    }, []);

    const snapToGrid = useCallback((x, y) => {
        const gridX = Math.round(x / gridSize) * gridSize;
        const gridY = Math.round(y / gridSize) * gridSize;
        return {
            x: Math.max(0, Math.min(gridX, canvasSize.width - gridSize)),
            y: Math.max(0, Math.min(gridY, canvasSize.height - gridSize))
        };
    }, [canvasSize, gridSize]);

    const getDeskFromDrag = useCallback((startX, startY, endX, endY) => {
        const startSnapped = snapToGrid(startX, startY);
        const endSnapped = snapToGrid(endX, endY);
        const minX = Math.min(startSnapped.x, endSnapped.x);
        const minY = Math.min(startSnapped.y, endSnapped.y);
        const maxX = Math.max(startSnapped.x, endSnapped.x);
        const maxY = Math.max(startSnapped.y, endSnapped.y);
        const width = Math.max(gridSize, maxX - minX + gridSize);
        const height = Math.max(gridSize, maxY - minY + gridSize);
        const finalWidth = Math.min(width, canvasSize.width - minX);
        const finalHeight = Math.min(height, canvasSize.height - minY);
        const cols = Math.round(finalWidth / gridSize);
        const rows = Math.round(finalHeight / gridSize);
        return {
            x: minX, y: minY, width: cols * gridSize, height: rows * gridSize,
            type: getDeskType(cols, rows), capacity: cols * rows
        };
    }, [getDeskType, snapToGrid, canvasSize, gridSize]);

    const checkOverlap = useCallback((x, y, width, height, excludeId = null) => {
        const deskOverlap = desks.some(desk => {
            if (excludeId && desk.id === excludeId) return false;
            return (x < desk.x + desk.width && x + width > desk.x && y < desk.y + desk.height && y + height > desk.y);
        });
        const podiumOverlap = podium && (x < podium.x + podium.width && x + width > podium.x && y < podium.y + podium.height && y + height > podium.y);
        return deskOverlap || podiumOverlap;
    }, [desks, podium]);

    const findAndMergeDesks = useCallback((newDesks) => {
        const allDesks = [...newDesks];
        let hasChanges = true;
        while (hasChanges) {
            hasChanges = false;
            for (let i = 0; i < allDesks.length && !hasChanges; i++) {
                for (let j = i + 1; j < allDesks.length && !hasChanges; j++) {
                    const desk1 = allDesks[i];
                    const desk2 = allDesks[j];
                    if (Math.abs(desk1.y - desk2.y) < 1 && Math.abs(desk1.height - desk2.height) < 1) {
                        const desk1Right = desk1.x + desk1.width;
                        const desk2Right = desk2.x + desk2.width;
                        if (Math.abs(desk1Right - desk2.x) < 1 || Math.abs(desk2Right - desk1.x) < 1) {
                            const minX = Math.min(desk1.x, desk2.x);
                            const cols1 = Math.round(desk1.width / gridSize);
                            const cols2 = Math.round(desk2.width / gridSize);
                            const rows = Math.round(desk1.height / gridSize);
                            const totalCols = cols1 + cols2;
                            const mergedDesk = {
                                id: Date.now() + Math.random(), x: minX, y: desk1.y,
                                width: totalCols * gridSize, height: rows * gridSize,
                                capacity: totalCols * rows, type: getDeskType(totalCols, rows)
                            };
                            allDesks.splice(Math.max(i, j), 1);
                            allDesks.splice(Math.min(i, j), 1);
                            allDesks.push(mergedDesk);
                            hasChanges = true;
                        }
                    }
                    if (Math.abs(desk1.x - desk2.x) < 1 && Math.abs(desk1.width - desk2.width) < 1 && !hasChanges) {
                        const desk1Bottom = desk1.y + desk1.height;
                        const desk2Bottom = desk2.y + desk2.height;
                        if (Math.abs(desk1Bottom - desk2.y) < 1 || Math.abs(desk2Bottom - desk1.y) < 1) {
                            const minY = Math.min(desk1.y, desk2.y);
                            const cols = Math.round(desk1.width / gridSize);
                            const rows1 = Math.round(desk1.height / gridSize);
                            const rows2 = Math.round(desk2.height / gridSize);
                            const totalRows = rows1 + rows2;
                            const mergedDesk = {
                                id: Date.now() + Math.random(), x: desk1.x, y: minY,
                                width: cols * gridSize, height: totalRows * gridSize,
                                capacity: cols * totalRows, type: getDeskType(cols, totalRows)
                            };
                            allDesks.splice(Math.max(i, j), 1);
                            allDesks.splice(Math.min(i, j), 1);
                            allDesks.push(mergedDesk);
                            hasChanges = true;
                        }
                    }
                }
            }
        }
        return allDesks;
    }, [getDeskType, gridSize]);
    
const handleSeatingChartClick = useCallback((event) => {
    const canvas = seatingCanvasRef.current;
    if (!canvas) return;

    
    const rect = canvas.getBoundingClientRect();
    const horizontalStretch = 1.4;
    const margin = 30;

    const allDrawableObjects = podium ? [...desks, podium] : [...desks];
    if (allDrawableObjects.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    allDrawableObjects.forEach(obj => {
        minX = Math.min(minX, obj.x);
        minY = Math.min(minY, obj.y);
        maxX = Math.max(maxX, obj.x + obj.width);
        maxY = Math.max(maxY, obj.y + obj.height);
    });

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    const availableWidth = canvas.width - margin * 2;
    const availableHeight = canvas.height - margin * 2;
    const finalContentWidth = contentWidth * horizontalStretch;
    const scale = Math.min(availableWidth / finalContentWidth, availableHeight / contentHeight);

    const finalScaleX = scale * horizontalStretch;
    const finalScaleY = scale;
    
    const scaledContentWidth = contentWidth * finalScaleX;
    const scaledContentHeight = contentHeight * finalScaleY;
    const offsetX = margin + (availableWidth - scaledContentWidth) / 2;
    const offsetY = margin + (availableHeight - scaledContentHeight) / 2;

    const canvasX = (event.clientX - rect.left) * (canvas.width / rect.width);
    const canvasY = (event.clientY - rect.top) * (canvas.height / rect.height);
    const worldX = (canvasX - offsetX) / finalScaleX + minX;
    const worldY = (canvasY - offsetY) / finalScaleY + minY;

    let clickedItem = null;
    for (const deskId in seatingArrangement) {
        const desk = desks.find(d => d.id.toString() === deskId);
        if (desk && worldX >= desk.x && worldX <= desk.x + desk.width && worldY >= desk.y && worldY <= desk.y + desk.height) {
            const studentsInDesk = seatingArrangement[deskId];
            if (studentsInDesk && studentsInDesk.length > 0) {
                    const cols = Math.round(desk.width / gridSize);
                    const rows = Math.round(desk.height / gridSize);
                    const cellWidth = desk.width / cols;
                    const cellHeight = desk.height / rows;
                    const localX = worldX - desk.x;
                    const localY = worldY - desk.y;
                    const colIndex = Math.floor(localX / cellWidth);
                    const rowIndex = Math.floor(localY / cellHeight);
                    const studentIndex = rowIndex * cols + colIndex;
                    if(studentsInDesk[studentIndex]) {
                        clickedItem = {
                            student: studentsInDesk[studentIndex],
                            deskId: parseInt(deskId),
                        };
                        break;
                    }
            }
        }
    }

    if (!selectedForSwap) {
        if (clickedItem) {
            setSelectedForSwap({
                studentId: clickedItem.student.id,
                deskId: clickedItem.deskId,
            });
        }
    } else {
        if (clickedItem && clickedItem.student.id !== selectedForSwap.studentId) {
            const newArrangement = JSON.parse(JSON.stringify(seatingArrangement));
            
            const student1 = students.find(s => s.id === selectedForSwap.studentId);
            const student2 = clickedItem.student;

            if (student1 && student2) {
                const desk1Arr = newArrangement[selectedForSwap.deskId];
                const desk2Arr = newArrangement[clickedItem.deskId];
                
                const index1 = desk1Arr.findIndex(s => s.id === student1.id);
                const index2 = desk2Arr.findIndex(s => s.id === student2.id);

                if (index1 !== -1 && index2 !== -1) {
                    desk1Arr[index1] = student2;
                    desk2Arr[index2] = student1;
                    setSeatingArrangement(newArrangement);
                }
            }
        }
        setSelectedForSwap(null);
    }
}, [desks, podium, seatingArrangement, selectedForSwap, students, gridSize]);

const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    const newWidth = Math.round(rect.width * dpr);
    const newHeight = Math.round(rect.height * dpr);

    if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth;
        canvas.height = newHeight;
    }

    const scaleX = rect.width / canvasSize.width;
    const scaleY = rect.height / canvasSize.height;
    
    ctx.setTransform(scaleX * dpr, 0, 0, scaleY * dpr, 0, 0);

    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    for (let x = 0; x <= canvasSize.width; x += gridSize) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvasSize.height); ctx.stroke(); }
    for (let y = 0; y <= canvasSize.height; y += gridSize) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvasSize.width, y); ctx.stroke(); }
    
    if (podium) {
        ctx.fillStyle = colorSettings.podium;
        ctx.fillRect(podium.x, podium.y, podium.width, podium.height);
        ctx.strokeStyle = colorSettings.podium; // Ramme = fyllfarge
        ctx.lineWidth = 3;
        ctx.strokeRect(podium.x, podium.y, podium.width, podium.height);
        
        const fontStyle = textStyle.italic ? 'italic' : 'normal';
        const fontWeight = textStyle.bold ? '700' : 'normal';
        ctx.fillStyle = colorSettings.textColor;
        ctx.font = `${fontStyle} ${fontWeight} bold 16px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('KATETER', podium.x + podium.width / 2, podium.y + podium.height / 2 + 2);
    }

    desks.forEach(desk => {
        const color = getDeskColor(desk.type); // Dette er kun for redigeringsmodus-fargene
        ctx.fillStyle = color;
        ctx.fillRect(desk.x, desk.y, desk.width, desk.height);
        ctx.strokeStyle = '#374151'; // Fast ramme-farge i redigering
        ctx.lineWidth = 2;
        ctx.strokeRect(desk.x, desk.y, desk.width, desk.height);
        
        const fontStyle = textStyle.italic ? 'italic' : 'normal';
        const fontWeight = textStyle.bold ? '700' : 'normal';
        ctx.fillStyle = colorSettings.textColor;
        ctx.font = `${fontStyle} ${fontWeight} bold 16px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(getDeskLabel(desk.type, desk.capacity), desk.x + desk.width / 2, desk.y + desk.height / 2 + 2);
    });
    
    if (isDrawing && dragStart && dragEnd) {
        if (showPodiumPlacement) {
            const startSnapped = snapToGrid(dragStart.x, dragStart.y);
            const endSnapped = snapToGrid(dragEnd.x, dragEnd.y);
            const minX = Math.min(startSnapped.x, endSnapped.x);
            const maxX = Math.max(startSnapped.x, endSnapped.x);
            const previewPodium = { x: minX, y: startSnapped.y, width: Math.max(gridSize * 2, maxX - minX + gridSize), height: Math.round((gridSize * 2) / 3) };
            ctx.fillStyle = 'rgba(139, 92, 246, 0.8)';
            ctx.fillRect(previewPodium.x, previewPodium.y, previewPodium.width, previewPodium.height);
        } else {
            const preview = getDeskFromDrag(dragStart.x, dragStart.y, dragEnd.x, dragEnd.y);
            ctx.fillStyle = 'rgba(59, 130, 246, 0.6)';
            ctx.fillRect(preview.x, preview.y, preview.width, preview.height);
        }
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [desks, podium, isDrawing, dragStart, dragEnd, canvasSize, gridSize, getDeskColor, getDeskLabel, getDeskFromDrag, snapToGrid, windowSize, showPodiumPlacement, colorSettings, textStyle]);

const drawSeatingCanvas = useCallback(() => {
    const canvas = seatingCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const newWidth = Math.round(rect.width * dpr);
    const newHeight = Math.round(rect.height * dpr);
    if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth;
        canvas.height = newHeight;
    }
    ctx.scale(dpr, dpr);
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    const horizontalStretch = 1.4;
    const margin = 30;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, displayWidth, displayHeight);
    const allDrawableObjects = podium ? [...desks, podium] : [...desks];
    if (allDrawableObjects.length === 0) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    allDrawableObjects.forEach(obj => {
        minX = Math.min(minX, obj.x);
        minY = Math.min(minY, obj.y);
        maxX = Math.max(maxX, obj.x + obj.width);
        maxY = Math.max(maxY, obj.y + obj.height);
    });
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const availableWidth = displayWidth - margin * 2;
    const availableHeight = displayHeight - margin * 2;
    const finalContentWidth = contentWidth * horizontalStretch;
    if (finalContentWidth <= 0 || contentHeight <= 0) return;
    const scale = Math.min(availableWidth / finalContentWidth, availableHeight / contentHeight);
    const finalScaleX = scale * horizontalStretch;
    const finalScaleY = scale;
    const scaledContentWidth = contentWidth * finalScaleX;
    const scaledContentHeight = contentHeight * finalScaleY;
    const offsetX = margin + (availableWidth - scaledContentWidth) / 2;
    const offsetY = margin + (availableHeight - scaledContentHeight) / 2;
    ctx.save();
    ctx.translate(offsetX - minX * finalScaleX, offsetY - minY * finalScaleY);
    ctx.scale(finalScaleX, finalScaleY);
    const avgScale = Math.sqrt(finalScaleX * finalScaleY);

    if (podium) {
        ctx.fillStyle = colorSettings.podium;
        ctx.fillRect(podium.x, podium.y, podium.width, podium.height);
        ctx.strokeStyle = colorSettings.podium;
        ctx.lineWidth = 2 / avgScale;
        ctx.strokeRect(podium.x, podium.y, podium.width, podium.height);
        
        ctx.save();
        const centerX = podium.x + podium.width / 2;
        const centerY = podium.y + podium.height / 2;
        ctx.translate(centerX, centerY);
        ctx.scale(1 / horizontalStretch, 1);
        
        const fontStyle = textStyle.italic ? 'italic' : 'normal';
        const fontWeight = textStyle.bold ? '700' : 'normal';
        ctx.fillStyle = colorSettings.textColor;
        const fontSize = 14 / finalScaleY;
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('KATETER', 0, 0);
        ctx.restore();
    }
    
    desks.forEach(desk => {
        const deskStudents = seatingArrangement[desk.id] || [];
        const cols = Math.round(desk.width / gridSize);
        const rows = Math.round(desk.height / gridSize);
        
        for (let i = 0; i < desk.capacity; i++) {
            const student = deskStudents[i];
            
            const col = i % cols;
            const row = Math.floor(i / cols);
            const cellX = desk.x + col * (desk.width / cols);
            const cellY = desk.y + row * (desk.height / rows);
            const cellWidth = desk.width / cols;
            const cellHeight = desk.height / rows;

            const fillColor = student ? getStudentColors(student).primary : colorSettings.gender.annet;

            ctx.fillStyle = fillColor;
            ctx.fillRect(cellX, cellY, cellWidth, cellHeight);

            ctx.strokeStyle = colorSettings.frame;
            ctx.lineWidth = 2 / avgScale;
            ctx.strokeRect(cellX, cellY, cellWidth, cellHeight);
            
            if (student) {
                if (selectedForSwap && selectedForSwap.studentId === student.id) {
                    ctx.strokeStyle = '#f59e0b';
                    ctx.lineWidth = 4 / avgScale;
                    ctx.strokeRect(cellX, cellY, cellWidth, cellHeight);
                }
                
                ctx.save();
                const centerX = cellX + cellWidth / 2;
                const centerY = cellY + cellHeight / 2;
                ctx.translate(centerX, centerY);
                ctx.scale(1 / horizontalStretch, 1);
                
                const fontStyle = textStyle.italic ? 'italic' : 'normal';
                const fontWeight = textStyle.bold ? '700' : '500';
                ctx.fillStyle = colorSettings.textColor;
                const fontSize = 14 / finalScaleY;
                ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px system-ui, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(student.name, 0, 0);
                
                ctx.restore();
            }
        }
    });
    ctx.restore();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [desks, podium, seatingArrangement, gridSize, selectedForSwap, getStudentColors, windowSize, colorSettings, textStyle]);

const drawCanvasForExport = useCallback((ctx, classroomName) => {
    const { width, height } = canvasSize;
    const horizontalStretch = 1.4;
    const margin = 30;
    const titleHeight = 40;
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, width, height);
    const allDrawableObjects = podium ? [...desks, podium] : [...desks];
    if (allDrawableObjects.length === 0) {
        ctx.fillStyle = colorSettings.textColor;
        ctx.font = 'bold 24px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(classroomName, width / 2, height / 2);
        return;
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    allDrawableObjects.forEach(obj => {
        minX = Math.min(minX, obj.x);
        minY = Math.min(minY, obj.y);
        maxX = Math.max(maxX, obj.x + obj.width);
        maxY = Math.max(maxY, obj.y + obj.height);
    });
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const availableWidth = width - margin * 2;
    const availableHeight = height - margin * 2 - titleHeight;
    const finalContentWidth = contentWidth * horizontalStretch;
    if (finalContentWidth <= 0 || contentHeight <= 0) return;
    const scale = Math.min(availableWidth / finalContentWidth, availableHeight / contentHeight);
    const finalScaleX = scale * horizontalStretch;
    const finalScaleY = scale;
    const scaledContentWidth = contentWidth * finalScaleX;
    const scaledContentHeight = contentHeight * finalScaleY;
    const offsetX = margin + (availableWidth - scaledContentWidth) / 2;
    const offsetY = margin + titleHeight + (availableHeight - scaledContentHeight) / 2;
    const fontStyle = textStyle.italic ? 'italic' : 'normal';
    const titleFontWeight = textStyle.bold ? '900' : 'bold';
    ctx.fillStyle = colorSettings.textColor;
    ctx.font = `${fontStyle} ${titleFontWeight} 24px system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(classroomName, margin, margin);
    ctx.save();
    ctx.translate(offsetX - minX * finalScaleX, offsetY - minY * finalScaleY);
    ctx.scale(finalScaleX, finalScaleY);
    const avgScale = Math.sqrt(finalScaleX * finalScaleY);

    if (podium) {
        ctx.fillStyle = colorSettings.podium;
        ctx.fillRect(podium.x, podium.y, podium.width, podium.height);
        ctx.strokeStyle = colorSettings.podium;
        ctx.lineWidth = 2 / avgScale;
        ctx.strokeRect(podium.x, podium.y, podium.width, podium.height);
        
        ctx.save();
        const centerX = podium.x + podium.width / 2;
        const centerY = podium.y + podium.height / 2;
        ctx.translate(centerX, centerY);
        ctx.scale(1 / horizontalStretch, 1);
        
        const fontWeight = textStyle.bold ? '700' : 'normal';
        ctx.fillStyle = colorSettings.textColor;
        const fontSize = 14 / finalScaleY;
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('KATETER', 0, 0);
        ctx.restore();
    }
    
    desks.forEach(desk => {
        const deskStudents = seatingArrangement[desk.id] || [];
        const cols = Math.round(desk.width / gridSize);
        const rows = Math.round(desk.height / gridSize);
        
        for (let i = 0; i < desk.capacity; i++) {
            const student = deskStudents[i];
            
            const col = i % cols;
            const row = Math.floor(i / cols);
            const cellX = desk.x + col * (desk.width / cols);
            const cellY = desk.y + row * (desk.height / rows);
            const cellWidth = desk.width / cols;
            const cellHeight = desk.height / rows;

            const fillColor = student ? getStudentColors(student).primary : colorSettings.gender.annet;

            ctx.fillStyle = fillColor;
            ctx.fillRect(cellX, cellY, cellWidth, cellHeight);

            ctx.strokeStyle = colorSettings.frame;
            ctx.lineWidth = 2 / avgScale;
            ctx.strokeRect(cellX, cellY, cellWidth, cellHeight);
            
            if (student) {
                ctx.save();
                const centerX = cellX + cellWidth / 2;
                const centerY = cellY + cellHeight / 2;
                ctx.translate(centerX, centerY);
                ctx.scale(1 / horizontalStretch, 1);
                
                const fontWeight = textStyle.bold ? '700' : '500';
                ctx.fillStyle = colorSettings.textColor;
                const fontSize = 14 / finalScaleY;
                ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px system-ui, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(student.name, 0, 0);
                
                ctx.restore();
            }
        }
    });
    ctx.restore();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [desks, podium, seatingArrangement, canvasSize, gridSize, getStudentColors, colorSettings, textStyle]);

    const handleCanvasMouseDown = useCallback((e) => {
        if (currentView !== 'setup') return;
        e.preventDefault();
        const coords = getCanvasCoords(e);
        setIsDrawing(true);
        setDragStart(coords);
        setDragEnd(coords);
    }, [currentView, getCanvasCoords]);

    const handleCanvasMouseMove = useCallback((e) => {
        if (currentView !== 'setup' || !isDrawing || !dragStart) return;
        e.preventDefault();
        const coords = getCanvasCoords(e);
        setDragEnd(coords);
    }, [currentView, isDrawing, dragStart, getCanvasCoords]);

const sortStudentsByName = () => {
    const sortedStudents = [...students].sort((a, b) =>
        a.name.localeCompare(b.name, 'no')
    );
    setStudents(sortedStudents);
};

const sortStudentsByGroup = () => {
    const sortedStudents = [...students].sort((a, b) => {
        const groupOrder = { 'A': 1, 'B': 2 };
        const groupA_order = a.group ? groupOrder[a.group] : 3;
        const groupB_order = b.group ? groupOrder[b.group] : 3;
        if (groupA_order !== groupB_order) {
            return groupA_order - groupB_order;
        }
        return a.name.localeCompare(b.name, 'no');
    });
    
    setStudents(sortedStudents);
};

const handleCanvasMouseUp = useCallback((e) => {
    if (currentView !== 'setup' || !isDrawing || !dragStart) return;
    e.preventDefault();
    const coords = getCanvasCoords(e);

    if (showPodiumPlacement) {
        const startSnapped = snapToGrid(dragStart.x, dragStart.y);
        const endSnapped = snapToGrid(coords.x, coords.y);
        const minX = Math.min(startSnapped.x, endSnapped.x);
        const maxX = Math.max(startSnapped.x, endSnapped.x);
        const newPodiumHeight = Math.round((gridSize * 2) / 3);
        const newPodium = {
            x: minX,
            y: startSnapped.y + (gridSize - newPodiumHeight) / 2,
            width: Math.max(gridSize * 2, maxX - minX + gridSize),
            height: newPodiumHeight
        };
        
        if (!checkOverlap(newPodium.x, newPodium.y, newPodium.width, newPodium.height) && newPodium.y + newPodium.height <= canvasSize.height) {
            setPodium(newPodium);
        }
        setShowPodiumPlacement(false);

    } else {
        const distance = Math.sqrt(Math.pow(coords.x - dragStart.x, 2) + Math.pow(coords.y - dragStart.y, 2));
        let newDesk;

        if (distance < gridSize / 2) {
            const cols = 1;
            const rows = 1;
            newDesk = {
                id: Date.now(),
                x: dragStart.x,
                y: dragStart.y,
                width: cols * gridSize,
                height: rows * gridSize,
                type: getDeskType(cols, rows),
                capacity: cols * rows
            };
        } else {
            const deskData = getDeskFromDrag(dragStart.x, dragStart.y, coords.x, coords.y);
            newDesk = { id: Date.now(), ...deskData };
        }

        if (newDesk.x >= 0 && newDesk.y >= 0 && newDesk.x + newDesk.width <= canvasSize.width && newDesk.y + newDesk.height <= canvasSize.height && newDesk.width > 0 && newDesk.height > 0 && !checkOverlap(newDesk.x, newDesk.y, newDesk.width, newDesk.height)) {
            const tempDesks = [...desks, newDesk];
            const mergedDesks = findAndMergeDesks(tempDesks);
            setDesks(mergedDesks);
            setSeatingArrangement(prev => {
                const newArrangement = { ...prev };
                const currentIds = new Set(mergedDesks.map(d => d.id));
                Object.keys(newArrangement).forEach(id => {
                    if (!currentIds.has(parseInt(id))) {
                        delete newArrangement[id];
                    }
                });
                return newArrangement;
            });
        }
    }
    setIsDrawing(false);
    setDragStart(null);
    setDragEnd(null);
}, [currentView, isDrawing, dragStart, showPodiumPlacement, getCanvasCoords, getDeskFromDrag, desks, checkOverlap, canvasSize, findAndMergeDesks, gridSize, snapToGrid, getDeskType, setPodium, setShowPodiumPlacement, setDesks, setSeatingArrangement, setIsDrawing, setDragStart, setDragEnd]);


const handleCanvasRightClick = useCallback((e) => {
    if (currentView !== 'setup') return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) * (canvas.width / rect.width);
    const mouseY = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (podium && mouseX >= podium.x && mouseX < podium.x + podium.width && mouseY >= podium.y && mouseY < podium.y + podium.height) {
        setPodium(null);
        return;
    }

    const coords = getCanvasCoords(e);
    const clickedDesk = desks.find(desk => coords.x >= desk.x && coords.x < desk.x + desk.width && coords.y >= desk.y && coords.y < desk.y + desk.height);
    if (clickedDesk) {
        setDesks(prev => prev.filter(d => d.id !== clickedDesk.id));
        setSeatingArrangement(prev => {
            const newArrangement = { ...prev };
            delete newArrangement[clickedDesk.id];
            return newArrangement;
        });
    }
}, [currentView, getCanvasCoords, desks, podium]);
    
    const assignRandomGroups = () => {
        const unassigned = students.filter(s => !s.group);
        const shuffled = [...unassigned].sort(() => 0.5 - Math.random());

        let groupACount = students.filter(s => s.group === 'A').length;
        let groupBCount = students.filter(s => s.group === 'B').length;

        const updatedStudents = students.map(s => {
            const studentToAssign = shuffled.find(sh => sh.id === s.id);
            if (studentToAssign) {
                if (groupACount <= groupBCount) {
                    groupACount++;
                    return { ...s, group: 'A' };
                } else {
                    groupBCount++;
                    return { ...s, group: 'B' };
                }
            }
            return s;
        });
        setStudents(updatedStudents);
    };

    const assignStudentToGroup = (studentId, group) => {
        setStudents(prevStudents =>
            prevStudents.map(s => s.id === studentId ? { ...s, group: group } : s)
        );
    };

    const clearAllGroups = () => {
        if(window.confirm("Er du sikker på at du vil fjerne alle fra gruppene?")) {
            setStudents(prevStudents =>
                prevStudents.map(s => ({ ...s, group: null }))
            );
        }
    };

    const unassignedStudents = useMemo(() => students.filter(s => !s.group), [students]);
    const groupA = useMemo(() => students.filter(s => s.group === 'A'), [students]);
    const groupB = useMemo(() => students.filter(s => s.group === 'B'), [students]);
    
const attemptSeatingArrangement = useCallback((studentsToPlace) => {
    const arrangement = {};
    const placedStudentIds = new Set();
    const availableDesks = [...desks];

    const getDeskZone = (desk) => {
        if (!desks || desks.length === 0) return {};
        const bounds = desks.reduce((acc, d) => ({
    minX: Math.min(acc.minX, d.x), minY: Math.min(acc.minY, d.y),
    maxX: Math.max(acc.maxX, d.x + d.width), maxY: Math.max(acc.maxY, d.y + d.height),
}), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

        const roomWidth = bounds.maxX - bounds.minX;
        const roomHeight = bounds.maxY - bounds.minY;
        const centerX = desk.x + desk.width / 2;
        const centerY = desk.y + desk.height / 2;

        let vPos = 'mid';
        if (roomHeight > 0 && centerY < bounds.minY + roomHeight / 3) vPos = 'top';
        else if (roomHeight > 0 && centerY > bounds.minY + (roomHeight * 2 / 3)) vPos = 'bottom';
        let hPos = 'Center';
        if (roomWidth > 0 && centerX < bounds.minX + roomWidth / 3) hPos = 'Left';
        else if (roomWidth > 0 && centerX > bounds.minX + (roomWidth * 2 / 3)) hPos = 'Right';
        return { [`${vPos}${hPos}`]: true };
    };

    const studentProcessingOrder = [...studentsToPlace].sort((a, b) => (b.placement?.length || 0) - (a.placement?.length || 0));

    studentProcessingOrder.forEach(student => {
        if (placedStudentIds.has(student.id)) return;

        let bestDeskId = null;
        let bestScore = -Infinity;

        availableDesks.forEach(desk => {
            const occupants = arrangement[desk.id] || [];
            if (occupants.length >= desk.capacity) return;

            let currentScore = 1000;

            occupants.forEach(occupant => {
                const pair = [student.id, occupant.id].sort((a,b)=>a-b);
                if (studentRelationships.cannotSitAdjacent.some(p => p[0] === pair[0] && p[1] === pair[1])) currentScore -= 2000;
                if (studentRelationships.mustSitTogether.some(p => p[0] === pair[0] && p[1] === pair[1])) currentScore += 1500;
            });
            
            if (seatingRules.prioritizePlacement && Array.isArray(student.placement) && student.placement.length > 0) {
                const deskZone = getDeskZone(desk);
                const mustPrefs = student.placement.filter(p => p.strength === 'must');
                const shouldPrefs = student.placement.filter(p => p.strength === 'should');

                if (mustPrefs.length > 0 && !mustPrefs.some(p => deskZone[p.position])) {
                    currentScore -= 20000;
                }
                if (shouldPrefs.some(p => deskZone[p.position])) {
                    currentScore += 500;
                }
            }
            
            currentScore += Math.random();

            if (currentScore > bestScore) {
                bestScore = currentScore;
                bestDeskId = desk.id;
            }
        });

        if (bestDeskId) {
            if (!arrangement[bestDeskId]) arrangement[bestDeskId] = [];
            arrangement[bestDeskId].push(student);
            placedStudentIds.add(student.id);
        } else {
             const firstAvailableDesk = desks.find(d => (arrangement[d.id] || []).length < d.capacity);
             if (firstAvailableDesk) {
                if (!arrangement[firstAvailableDesk.id]) arrangement[firstAvailableDesk.id] = [];
                arrangement[firstAvailableDesk.id].push(student);
                placedStudentIds.add(student.id);
             }
        }
    });

    return arrangement;
}, [desks, seatingRules, studentRelationships]);

    const evaluateArrangement = useCallback((arrangement) => {
    let score = 0;
    const allPlacedStudents = Object.values(arrangement).flat();

    (studentRelationships.mustSitTogether || []).forEach(([s1Id, s2Id]) => {
        const d1 = Object.keys(arrangement).find(dId => arrangement[dId]?.some(s => s.id === s1Id));
        const d2 = Object.keys(arrangement).find(dId => arrangement[dId]?.some(s => s.id === s2Id));
        if (d1 === d2 && d1) {
            score += 500;
        } else if (allPlacedStudents.some(s => s.id === s1Id) || allPlacedStudents.some(s => s.id === s2Id)) {
            score -= 1000;
        }
    });
    (studentRelationships.cannotSitAdjacent || []).forEach(([s1Id, s2Id]) => {
        const d1 = Object.keys(arrangement).find(dId => arrangement[dId]?.some(s => s.id === s1Id));
        const d2 = Object.keys(arrangement).find(dId => arrangement[dId]?.some(s => s.id === s2Id));
        if (d1 === d2 && d1) {
            score -= 800;
        }
    });
    (studentRelationships.cannotSitNearby || []).forEach(([s1Id, s2Id]) => {
        const d1Info = Object.entries(arrangement).find(([, sArr]) => sArr?.some(s => s.id === s1Id));
        const d2Info = Object.entries(arrangement).find(([, sArr]) => sArr?.some(s => s.id === s2Id));
        if (d1Info && d2Info) {
            const [d1Id] = d1Info; const [d2Id] = d2Info;
            const desk1 = desks.find(d => d.id.toString() === d1Id);
            const desk2 = desks.find(d => d.id.toString() === d2Id);
            if (desk1 && desk2) {
                const dist = Math.sqrt(Math.pow(desk1.x - desk2.x, 2) + Math.pow(desk1.y - desk2.y, 2));
                if (dist <= gridSize * 2.5) {
                    score -= 400;
                }
            }
        }
    });
    if (seatingRules.alternateGender) {
        Object.values(arrangement).forEach(sArr => {
            if (sArr && sArr.length > 1) {
                for (let i = 1; i < sArr.length; i++) {
                    if (sArr[i].gender !== sArr[i - 1].gender) {
                        score += 30;
                    }
                }
            }
        });
    }

    if (seatingRules.prioritizePlacement && desks.length > 0) {
        const deskBounds = desks.reduce((acc, desk) => {
            acc.minX = Math.min(acc.minX, desk.x);
            acc.minY = Math.min(acc.minY, desk.y);
            acc.maxX = Math.max(acc.maxX, desk.x + desk.width);
            acc.maxY = Math.max(acc.maxY, desk.y + desk.height);
            return acc;
        }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

        const roomWidth = deskBounds.maxX - deskBounds.minX;
        const roomHeight = deskBounds.maxY - deskBounds.minY;

        Object.entries(arrangement).forEach(([deskId, studentsInDesk]) => {
            const desk = desks.find(d => d.id.toString() === deskId);
            if (!desk || !studentsInDesk) return;

            const deskCenterX = desk.x + desk.width / 2;
            const deskCenterY = desk.y + desk.height / 2;

            studentsInDesk.forEach(student => {
                if (!student || !Array.isArray(student.placement) || student.placement.length === 0) return;

                const preferences = student.placement;
                const mustPreferences = preferences.filter(p => p.strength === 'must');
                const shouldPreferences = preferences.filter(p => p.strength === 'should');

                const isMatch = (position) => {
                    let verticalMatch = false;
                    if (position.includes('top') && deskCenterY < deskBounds.minY + roomHeight / 3) verticalMatch = true;
                    if (position.includes('mid') && deskCenterY >= deskBounds.minY + roomHeight / 3 && deskCenterY <= deskBounds.minY + (roomHeight * 2 / 3)) verticalMatch = true;
                    if (position.includes('bottom') && deskCenterY > deskBounds.minY + (roomHeight * 2 / 3)) verticalMatch = true;
                    
                    let horizontalMatch = false;
                    if (position.includes('Left') && deskCenterX < deskBounds.minX + roomWidth / 3) horizontalMatch = true;
                    if (position.includes('Center') && deskCenterX >= deskBounds.minX + roomWidth / 3 && deskCenterX <= deskBounds.minX + (roomWidth * 2 / 3)) horizontalMatch = true;
                    if (position.includes('Right') && deskCenterX > deskBounds.minX + (roomWidth * 2 / 3)) horizontalMatch = true;
                    
                    return verticalMatch && horizontalMatch;
                };

                if (mustPreferences.length > 0) {
                    const anyMustMatch = mustPreferences.some(p => isMatch(p.position));
                    if (!anyMustMatch) {
                        score -= 10000;
                    }
                }

                if (shouldPreferences.length > 0) {
                    const anyShouldMatch = shouldPreferences.some(p => isMatch(p.position));
                    if (anyShouldMatch) {
                        score += 250;
                    }
                }
            });
        });
    }

    return score;
}, [desks, seatingRules, studentRelationships, gridSize]);
    
    const generateAdvancedSeating = () => {
        const studentsToPlace = students.filter(s => {
            if (groupFilter === 'all') return true;
            return s.group === groupFilter;
        });

        const totalCapacity = desks.reduce((sum, desk) => sum + desk.capacity, 0);
        if (studentsToPlace.length > totalCapacity) {
            alert(`For mange elever (${studentsToPlace.length}) for antall plasser i klasserommet (${totalCapacity})!`);
            return;
        }
        if (studentsToPlace.length === 0) {
            alert('Ingen elever å plassere (sjekk gruppe-filteret)!');
            return;
        }
        if (desks.length === 0) {
            alert('Ingen pulter å plassere elever på!');
            return;
        }
        
        let attempts = 0;
        const maxAttempts = 200;
        let bestArrangement = null;
        let bestScore = -Infinity;
        
        while (attempts < maxAttempts) {
            const arrangement = attemptSeatingArrangement(studentsToPlace);
            const score = evaluateArrangement(arrangement);

            if (score > bestScore) {
                bestScore = score;
                bestArrangement = arrangement;
            }
            attempts++;
        }

        if (bestArrangement) {
            setSeatingArrangement(bestArrangement);
        } else {
            alert('Kunne ikke generere en gyldig sitteplan. Prøv igjen.');
        }
    };
    
 useEffect(() => {
        if (currentView === 'setup') {
            drawCanvas();
        } else if (currentView === 'seating') {
            drawSeatingCanvas();
        }
    }, [currentView, desks, podium, isDrawing, dragStart, dragEnd, seatingArrangement, drawCanvas, drawSeatingCanvas]);

    return (
        <>
        <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #print-area, #print-area * {
                            visibility: visible;
                        }
                        #print-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                            padding: 1rem;
                        }
                        .group-card {
                            border: 1px solid #ccc !important;
                            page-break-inside: avoid;
                            box-shadow: none !important;
                        }
                        h3 {
                            font-size: 1.5rem;
                            margin-top: 1.5rem;
                            margin-bottom: 1rem;
                        }
                    }
                `}
            </style>
            
        <div className="min-h-screen bg-gray-50">
            {isRosterModalOpen && (
                <RosterManager
                    rosters={rosters}
                    setRosters={setRosters}
                    currentStudents={students}
                    currentRelationships={studentRelationships}
                    onRosterLoad={handleRosterLoad}
                    onClose={() => setIsRosterModalOpen(false)}
                    currentGroups={groups}
                />
            )}

            {editingPlacementFor && (
                <PlacementManager
                    student={editingPlacementFor}
                    onClose={() => setEditingPlacementFor(null)}
                    onSave={handleSavePlacement}
                />
            )}
            
            <div className="bg-white shadow-md border-b relative z-60">
                 <div className="max-w-6xl mx-auto px-6 py-4">
                     <div className="flex flex-col items-center gap-4">
                         <h1 className="text-2xl font-bold text-gray-900">Klassekart-generator</h1>
                         <div className="flex justify-between items-center w-full gap-4">
                             {currentClassroom ? (
                                 <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-2">
                                     <select value={currentClassroom} onChange={(e) => switchClassroom(e.target.value)} className="border border-gray-300 rounded px-3 py-1 text-sm">
                                         {Object.entries(classrooms).map(([id, classroom]) => (<option key={id} value={id}>{classroom.name}</option>))}
                                     </select>
                                     <button onClick={createNewClassroom} className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600" title="Opprett nytt klasserom"><Plus size={14} /></button>
                                     <button onClick={() => { const newName = prompt('Nytt navn:', classrooms[currentClassroom]?.name); if (newName) renameClassroom(newName); }} className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600" title="Gi nytt navn">✏️</button>
                                     {Object.keys(classrooms).length > 1 && (<button onClick={() => deleteClassroom(currentClassroom)} className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600" title="Slett klasserom">🗑️</button>)}
                                 </div>
                             ) : <div></div>}
                             <div className="flex justify-center gap-3 flex-wrap">
                                 <button onClick={() => setIsRosterModalOpen(true)} className="flex items-center gap-2 px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                                     <BookCopy size={14} /> Behandle Elevlister
                                 </button>
                                 <Dropdown buttonText="Eksporter" icon={<Download size={14} />}>
                                     <button onClick={exportClassrooms} className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"><Settings size={14} /> Hele prosjektet (.json)</button>
                                     <button onClick={exportStudentRoster} className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"><Users size={14} /> Kun elevgruppe (.json)</button>
                                     <button onClick={exportStudentList} className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 border-t mt-1 pt-1"><Download size={14} /> Enkel elevliste (.csv)</button>
                                 </Dropdown>
                                 <Dropdown buttonText="Importer" icon={<Upload size={14} />}>
                                     <label className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer"><Settings size={14} /> Hele prosjektet (.json)<input type="file" accept=".json" onChange={importClassrooms} className="hidden" /></label>
                                     <label className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer"><Users size={14} /> Kun elevgruppe (.json)<input type="file" accept=".json" onChange={importStudentRoster} className="hidden" /></label>
                                     <label
                                         className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer border-t mt-1 pt-1"
                                         title="Filen må være en .csv med komma eller semikolon som skille..." >
                                         <Upload size={14} /> Enkel elevliste (.csv)
                                         <input type="file" accept=".csv" onChange={importStudentList} className="hidden" />
                                     </label>
                                 </Dropdown>
                             </div>
                         </div>
                     </div>
                 </div>
            </div>

          <div className="bg-white shadow-sm border-b sticky top-0 z-50 print:hidden">
    <div className="max-w-6xl mx-auto px-6 py-2 flex justify-center">
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setCurrentView('setup')} className={`px-4 py-1.5 rounded-md flex items-center gap-2 transition-colors ${currentView === 'setup' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
                <Settings size={16} /> Klasserom
            </button>
            <button onClick={() => setCurrentView('students')} className={`px-4 py-1.5 rounded-md flex items-center gap-2 transition-colors ${currentView === 'students' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
                <Users size={16} /> Elever
            </button>
            <button onClick={() => setCurrentView('seating')} className={`px-4 py-1.5 rounded-md flex items-center gap-2 transition-colors ${currentView === 'seating' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
                <LayoutGrid size={16} /> Klassekart
            </button>
            <button onClick={() => setCurrentView('groups')} className={`px-4 py-1.5 rounded-md flex items-center gap-2 transition-colors ${currentView === 'groups' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
                <Grid3x3 size={16} /> Gruppe-generator
            </button>
         </div>
    </div>
</div>
            
            <main>
                <div className="max-w-6xl mx-auto px-6 pt-4 pb-6">
                    {currentView === 'setup' && (
                         <div className="space-y-6">
                             <div className="flex justify-between items-center">
                                 <h2 className="text-2xl font-semibold text-gray-900">Klasseromsoppsett</h2>
                                 <div className="flex gap-2">
                                     <button onClick={() => setShowPodiumPlacement(!showPodiumPlacement)} className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${showPodiumPlacement ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                                         {showPodiumPlacement ? 'Avbryt kateter' : 'Plasser kateter'}
                                     </button>
                                     <button onClick={clearDesks} className="flex items-center gap-1 px-3 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 transition-colors"><RotateCcw size={14} /> Tøm alt</button>
                                 </div>
                             </div>
                             {showPodiumPlacement && (
                                 <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                     <div className="flex items-center gap-2 text-purple-700 mb-2"><AlertTriangle size={16} /><span className="font-medium">Kateter-plassering aktivt</span></div>
                                     <p className="text-sm text-purple-600">Klikk og dra for å plassere kateter i klasserommet.</p>
                                 </div>
                             )}
                             <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                                 <div className="flex justify-between items-center mb-4">
                                     <div className="flex gap-4 items-center text-sm text-gray-600">
                                         <span>{desks.length} pulter</span>
                                         <span>{desks.reduce((sum, desk) => sum + desk.capacity, 0)} plasser</span>
                                         {podium && <span className="text-purple-600">✓ Kateter</span>}
                                     </div>
                                 </div>
                                 <div style={{ position: 'relative', width: '100%', maxWidth: '800px', aspectRatio: '800 / 533', margin: '0 auto', border: '2px solid #374151', borderRadius: '8px', overflow: 'hidden' }}>
                                     <canvas ref={canvasRef} width={canvasSize.width} height={canvasSize.height} onMouseDown={handleCanvasMouseDown} onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp} onContextMenu={handleCanvasRightClick} className="cursor-crosshair w-full h-full block" style={{ touchAction: 'none', imageRendering: 'pixelated' }} />
                                 </div>
                                 <div className="mt-4 text-sm text-gray-600">
                                     <p><strong>Venstreklikk:</strong> Legg til {showPodiumPlacement ? 'kateter' : 'pult'}</p>
                                     <p><strong>Høyreklikk:</strong> Slett</p>
                                     <p><strong>Klikk og dra:</strong> Lag større {showPodiumPlacement ? 'kateter' : 'pulter'}</p>
                                 </div>
                             </div>
                         </div>
                    )}

                    {currentView === 'students' && (
                        <div className="space-y-6">

                            {isGroupingMode ? (
                                <div className="bg-white border rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-xl font-semibold text-gray-900">Del klassen i grupper</h3>
                                        <button onClick={() => setIsGroupingMode(false)} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm">
                                            <Check size={16} /> Ferdig
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="bg-gray-50 p-3 rounded-lg border">
                                            <h4 className="font-medium text-center mb-3">Uplasserte elever ({unassignedStudents.length})</h4>
                                            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                                                {unassignedStudents.map(s => (
                                                    <div key={s.id} className="bg-white p-2 rounded shadow-sm text-sm flex justify-between items-center">
                                                        <span>{s.name}</span>
                                                        <div className="flex-shrink-0">
                                                            <button title={`Flytt til ${groups.A}`} onClick={() => assignStudentToGroup(s.id, 'A')} className="mr-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded hover:bg-blue-200">A</button>
                                                            <button title={`Flytt til ${groups.B}`} onClick={() => assignStudentToGroup(s.id, 'B')} className="px-2 py-0.5 bg-green-100 text-green-800 rounded hover:bg-green-200">B</button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                            <input
                                                type="text"
                                                value={groups.A}
                                                onChange={(e) => setGroups(prev => ({...prev, A: e.target.value}))}
                                                className="font-medium text-center mb-3 text-blue-800 bg-transparent w-full focus:bg-white focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
                                            />
                                            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                                                {groupA.map(s => (
                                                    <div key={s.id} className="bg-white p-2 rounded shadow-sm text-sm mb-2 flex justify-between items-center">
                                                        <span>{s.name}</span>
                                                        <button title="Fjern fra gruppe" onClick={() => assignStudentToGroup(s.id, null)} className="text-gray-400 hover:text-red-500"><X size={14}/></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                            <input
                                                type="text"
                                                value={groups.B}
                                                onChange={(e) => setGroups(prev => ({...prev, B: e.target.value}))}
                                                className="font-medium text-center mb-3 text-green-800 bg-transparent w-full focus:bg-white focus:ring-1 focus:ring-green-500 rounded px-2 py-1"
                                            />
                                            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                                                {groupB.map(s => (
                                                    <div key={s.id} className="bg-white p-2 rounded shadow-sm text-sm mb-2 flex justify-between items-center">
                                                        <span>{s.name}</span>
                                                        <button title="Fjern fra gruppe" onClick={() => assignStudentToGroup(s.id, null)} className="text-gray-400 hover:text-red-500"><X size={14}/></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex gap-2 justify-center">
                                        <button onClick={assignRandomGroups} className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600">Fordel tilfeldig</button>
                                        <button onClick={clearAllGroups} className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600">Tøm alle grupper</button>
                                    </div>
                                </div>
                            ) : editingRelationshipsFor ? (
                                <RelationshipEditor
                                    studentInFocus={editingRelationshipsFor}
                                    allStudents={students}
                                    relationships={studentRelationships}
                                    onUpdate={handleUpdateRelationship}
                                    onClose={() => setEditingRelationshipsFor(null)}
                                />
                            ) : (
                                <>
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-2xl font-semibold text-gray-900">Elever og relasjoner</h2>
                                        <div className="flex items-center gap-2">
                                            {students.length > 1 && (
                                                <Dropdown buttonText="Sorter etter..." icon={<ArrowDownAZ size={14} />}>
                                                    <button
                                                        onClick={sortStudentsByName}
                                                        className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                    >
                                                        <ArrowDownAZ size={14} /> Navn (A-Å)
                                                    </button>
                                                    <button
                                                        onClick={sortStudentsByGroup}
                                                        className="text-left w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                                    >
                                                        <Users2 size={14} /> Gruppe
                                                    </button>
                                                </Dropdown>
                                            )}
                                            {students.length > 0 && (
                                                <button onClick={() => setIsGroupingMode(true)} className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-yellow-900 rounded-lg hover:bg-yellow-300 font-semibold">
                                                    <Users2 size={16} /> Del klassen i grupper
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-medium text-gray-900 mb-3">Legg til elev</h3>
                                        <div className="space-y-3">
                                            <input type="text" placeholder="Navn på elev" value={newStudent.name} onChange={(e) => setNewStudent(prev => ({ ...prev, name: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2" onKeyPress={(e) => e.key === 'Enter' && addStudent()} />
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-gray-700">Kjønn:</div>
                                                <div className="flex gap-4">
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="gender" value="gutt" checked={newStudent.gender === 'gutt'} onChange={(e) => setNewStudent(prev => ({ ...prev, gender: e.target.value }))} className="text-blue-500" /><span className="text-sm">Gutt</span></label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="gender" value="jente" checked={newStudent.gender === 'jente'} onChange={(e) => setNewStudent(prev => ({ ...prev, gender: e.target.value }))} className="text-pink-500" /><span className="text-sm">Jente</span></label>
                                                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="gender" value="annet" checked={newStudent.gender === 'annet'} onChange={(e) => setNewStudent(prev => ({ ...prev, gender: e.target.value }))} className="text-gray-500" /><span className="text-sm">Annet</span></label>
                                                </div>
                                            </div>
                                            <button onClick={addStudent} disabled={!newStudent.name.trim()} className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Legg til elev</button>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-3">Elevliste ({students.length})</h3>
                                        {students.length === 0 ? (
                                            <p className="text-sm text-gray-500 italic">Ingen elever lagt til ennå</p>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {students.map((student) => (
                                                    <div key={student.id} className="bg-white border rounded-lg p-3">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="font-medium text-gray-900 flex items-center">
                                                                {student.name}
                                                                {student.group && (
                                                                    <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                                                        student.group === 'A' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                                                    }`}>
                                                                        {groups[student.group] || student.group}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    onClick={() => setEditingRelationshipsFor(student)}
                                                                    className={`transition-colors ${getRelationshipStatusColor(student.id)}`}
                                                                    title="Endre relasjoner"
                                                                >
                                                                    <Users size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingPlacementFor(student)}
                                                                    className={`transition-colors ${getPlacementStatusColor(student)}`}
                                                                    title="Endre posisjonsønsker"
                                                                >
                                                                    <MapPin size={16} />
                                                                </button>
                                                                <button onClick={() => removeStudent(student.id)} className="text-red-500 hover:text-red-700 px-2 py-1 transition-colors">✕</button>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-4">
                                                            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`gender-${student.id}`} value="gutt" checked={student.gender === 'gutt'} onChange={(e) => updateStudentGender(student.id, e.target.value)} className="text-blue-500" /><span className="text-sm">Gutt</span></label>
                                                            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`gender-${student.id}`} value="jente" checked={student.gender === 'jente'} onChange={(e) => updateStudentGender(student.id, e.target.value)} className="text-pink-500" /><span className="text-sm">Jente</span></label>
                                                            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name={`gender-${student.id}`} value="annet" checked={student.gender === 'annet'} onChange={(e) => updateStudentGender(student.id, e.target.value)} className="text-gray-500" /><span className="text-sm">Annet</span></label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
 {/* START PÅ NY GRUPPE-VISNING */}
{/* ===== START: NY, FORBEDRET VISNING FOR GRUPPE-GENERATOR ===== */}
                    {currentView === 'groups' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-semibold text-gray-900">Gruppe-generator</h2>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Innstillings-panel */}
                                <div className="lg:col-span-1 bg-white p-4 rounded-lg border space-y-6 h-fit">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-1">1. Velg metode</h3>
                                        <p className="text-xs text-gray-500 mb-2">Lag grupper for hele klassen, eller innad i delingsgruppene du har definert.</p>
                                        <label className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer has-[:disabled]:cursor-not-allowed has-[:disabled]:text-gray-400" title={groupA.length === 0 || groupB.length === 0 ? "Du må først dele klassen i A/B-grupper under 'Elever'-fanen" : ""}>
                                            <input 
                                                type="checkbox" 
                                                checked={groupGenSettings.generateWithinSubgroups} 
                                                onChange={e => setGroupGenSettings(p => ({...p, generateWithinSubgroups: e.target.checked}))}
                                                disabled={groupA.length === 0 || groupB.length === 0}
                                                className="rounded h-4 w-4 text-purple-600 focus:ring-purple-500"
                                            />
                                            <span className="text-sm">Lag grupper innad i delingsgruppene</span>
                                        </label>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-1">2. Velg inndeling</h3>
                                        <p className="text-xs text-gray-500 mb-2">Bestem enten antall grupper du vil ha, eller hvor mange elever som skal være i hver gruppe.</p>
                                        <div className="flex flex-col space-y-2">
                                            <label htmlFor="byCount" className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50">
                                                <input type="radio" id="byCount" name="method" value="byCount" checked={groupGenSettings.method === 'byCount'} onChange={e => setGroupGenSettings(p => ({...p, method: e.target.value}))} />
                                                <Grid3x3 size={16} className="text-gray-500"/>
                                                <span className="w-28 text-sm">Antall grupper:</span>
                                                <input type="number" min="2" max="20" value={groupGenSettings.count} onChange={e => setGroupGenSettings(p => ({...p, count: parseInt(e.target.value)}))} disabled={groupGenSettings.method !== 'byCount'} className="w-16 border-gray-300 rounded-md shadow-sm text-sm" />
                                            </label>
                                            <label htmlFor="bySize" className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50">
                                                <input type="radio" id="bySize" name="method" value="bySize" checked={groupGenSettings.method === 'bySize'} onChange={e => setGroupGenSettings(p => ({...p, method: e.target.value}))} />
                                                <Users size={16} className="text-gray-500"/>
                                                <span className="w-28 text-sm">Elever pr. gruppe:</span>
                                                <input type="number" min="2" max="10" value={groupGenSettings.size} onChange={e => setGroupGenSettings(p => ({...p, size: parseInt(e.target.value)}))} disabled={groupGenSettings.method !== 'bySize'} className="w-16 border-gray-300 rounded-md shadow-sm text-sm" />
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-1">3. Følg regler</h3>
                                        <p className="text-xs text-gray-500 mb-2">Generatoren vil alltid respektere "må/kan ikke"-relasjoner.</p>
                                        <label className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer">
                                            <input type="checkbox" checked={groupGenSettings.balanceGender} onChange={e => setGroupGenSettings(p => ({...p, balanceGender: e.target.checked}))} className="rounded h-4 w-4 text-purple-600 focus:ring-purple-500" />
                                            <span className="text-sm">Prøv å fordele kjønn jevnt</span>
                                        </label>
                                    </div>

                                    <button onClick={generateGroups} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-base">
                                        <Shuffle size={18} /> Generer Grupper
                                    </button>
                                </div>

                                {/* Resultat-panel */}
                                <div className="lg:col-span-2">
                                    {(!generatedGroups.all && !generatedGroups.A && !generatedGroups.B) || (generatedGroups.all?.length === 0 && generatedGroups.A?.length === 0 && generatedGroups.B?.length === 0) ? (
                                        <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded-lg border-2 border-dashed min-h-[300px] text-center p-4">
                                            <Grid3x3 size={48} className="text-gray-300 mb-4" />
                                            <h3 className="text-lg font-semibold text-gray-700">Gruppene dine vil vises her</h3>
                                            <p className="text-gray-500 text-sm">Still inn valgene til venstre og trykk 'Generer Grupper' for å se resultatet.</p>
                                        </div>
                                    ) : (
                                        <div id="print-area">
                                            <div className="flex justify-end gap-2 mb-4 print:hidden">
                                                <button onClick={copyGroupsToClipboard} className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm">
                                                    <Clipboard size={14} /> Kopier
                                                </button>
                                                <button onClick={exportGroupsAsTxt} className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm">
                                                    <Download size={14} /> Eksporter (.txt)
                                                </button>
                                                <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm">
                                                    <Printer size={14} /> Skriv ut
                                                </button>
                                            </div>
                                            
                                            <div className="space-y-6">
                                                {generatedGroups.all && generatedGroups.all.length > 0 && (
                                                    <div>
                                                        <h3 className="text-xl font-bold mb-3 text-gray-800">Grupper for hele klassen</h3>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                                            {generatedGroups.all.map((group, index) => (
                                                                <div key={index} className="bg-white rounded-lg border shadow-sm group-card overflow-hidden">
                                                                    <div className="p-3 bg-gray-50 border-b flex items-center gap-2">
                                                                        <Users2 size={16} className="text-gray-500" />
                                                                        <h4 className="font-bold text-gray-800">Gruppe {index + 1}</h4>
                                                                    </div>
                                                                    <ul className="p-3 space-y-2">
                                                                        {group.map(student => <li key={student.id} className="text-sm flex items-center gap-2"><span>{student.gender === 'gutt' ? '👨' : student.gender === 'jente' ? '👩' : '👤'}</span>{student.name}</li>)}
                                                                    </ul>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {(generatedGroups.A || generatedGroups.B) && (
                                                    <div className="space-y-8">
                                                        {generatedGroups.A && generatedGroups.A.length > 0 && (
                                                            <div>
                                                                <h3 style={{color: colorSettings.textColor, borderBottomColor: colorSettings.group.A}} className="text-xl font-bold mb-3 pb-2 border-b-4">{groups.A}</h3>
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                    {generatedGroups.A.map((group, index) => (
                                                                        <div key={`A-${index}`} className="bg-white rounded-lg border shadow-sm group-card overflow-hidden">
                                                                            <div style={{backgroundColor: colorSettings.group.A}} className="p-3 border-b flex items-center gap-2">
                                                                                <Users2 size={16} style={{color: colorSettings.textColor}} />
                                                                                <h4 className="font-bold" style={{color: colorSettings.textColor}}>Gruppe {index + 1}</h4>
                                                                            </div>
                                                                            <ul className="p-3 space-y-2">
                                                                                {group.map(student => <li key={student.id} className="text-sm flex items-center gap-2"><span>{student.gender === 'gutt' ? '👨' : student.gender === 'jente' ? '👩' : '👤'}</span>{student.name}</li>)}
                                                                            </ul>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {generatedGroups.B && generatedGroups.B.length > 0 && (
                                                            <div>
                                                                <h3 style={{color: colorSettings.textColor, borderBottomColor: colorSettings.group.B}} className="text-xl font-bold mb-3 pb-2 border-b-4">{groups.B}</h3>
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                    {generatedGroups.B.map((group, index) => (
                                                                        <div key={`B-${index}`} className="bg-white rounded-lg border shadow-sm group-card overflow-hidden">
                                                                             <div style={{backgroundColor: colorSettings.group.B}} className="p-3 border-b flex items-center gap-2">
                                                                                <Users2 size={16} style={{color: colorSettings.textColor}} />
                                                                                <h4 className="font-bold" style={{color: colorSettings.textColor}}>Gruppe {index + 1}</h4>
                                                                            </div>
                                                                            <ul className="p-3 space-y-2">
                                                                                {group.map(student => <li key={student.id} className="text-sm flex items-center gap-2"><span>{student.gender === 'gutt' ? '👨' : student.gender === 'jente' ? '👩' : '👤'}</span>{student.name}</li>)}
                                                                            </ul>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* ===== SLUTT: NY VISNING FOR GRUPPE-GENERATOR ===== */}

                    {currentView === 'seating' && (
                         <div className="space-y-6">
                             <div className="flex justify-between items-center">
                                 <h2 className="text-2xl font-semibold text-gray-900">Klassekart</h2>
                             </div>

                             <div className="bg-white p-4 rounded-lg border">
                                 <div className="flex justify-between items-center">
                                     <div className="flex items-center gap-4">
                                         <h3 className="text-lg font-medium text-gray-900">Filter:</h3>
                                         <div className="flex gap-2 bg-gray-200 rounded-lg p-1">
                                             <button onClick={() => setGroupFilter('all')} className={`px-4 py-1.5 rounded-md text-sm ${groupFilter === 'all' ? 'bg-blue-500 text-white shadow' : 'hover:bg-gray-300 text-gray-800'}`}>Hele klassen</button>
                                             <button onClick={() => setGroupFilter('A')} className={`px-4 py-1.5 rounded-md text-sm ${groupFilter === 'A' ? 'bg-blue-500 text-white shadow' : 'hover:bg-gray-300 text-gray-800'}`}>Kun {groups.A}</button>
                                             <button onClick={() => setGroupFilter('B')} className={`px-4 py-1.5 rounded-md text-sm ${groupFilter === 'B' ? 'bg-blue-500 text-white shadow' : 'hover:bg-gray-300 text-gray-800'}`}>Kun {groups.B}</button>
                                         </div>
                                     </div>

                                     <div className="flex gap-3">
                                         <button onClick={generateAdvancedSeating} className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"><Shuffle size={16} /> Generer klassekart</button>
                                         <button onClick={exportSeatingChart} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"><Download size={16} /> Eksporter som Bilde</button>
                                     </div>
                                 </div>
                             </div>
                             
                            <div className="bg-white p-4 rounded-lg border">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-3">Plasseringsregler</h3>
                                        <div className="space-y-2">
                                            <label className="flex items-center gap-2"><input type="checkbox" checked={seatingRules.alternateGender} onChange={(e) => setSeatingRules(prev => ({ ...prev, alternateGender: e.target.checked }))} className="rounded" /><span className="text-sm">Prøv å alternere kjønn</span></label>
                                            <label className="flex items-center gap-2"><input type="checkbox" checked={seatingRules.separateTroublesome} onChange={(e) => setSeatingRules(prev => ({ ...prev, separateTroublesome: e.target.checked }))} className="rounded" /><span className="text-sm">Prioriter å skille problematiske par</span></label>
                                            <label className="flex items-center gap-2"><input type="checkbox" checked={seatingRules.prioritizePlacement} onChange={(e) => setSeatingRules(prev => ({ ...prev, prioritizePlacement: e.target.checked }))} className="rounded" /><span className="text-sm">Prioriter posisjonsønsker</span></label>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-3">Visningsvalg</h3>
                                        <div className="space-y-2">
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={chartOptions.showGenderColors} onChange={(e) => setChartOptions(prev => ({ ...prev, showGroupColors: false, showGenderColors: e.target.checked }))} className="rounded" /><span className="text-sm">Vis farger for kjønn</span></label>
                                            {students.some(s => s.group) && (<label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={chartOptions.showGroupColors} onChange={(e) => setChartOptions(prev => ({ ...prev, showGenderColors: false, showGroupColors: e.target.checked }))} className="rounded" /><span className="text-sm">Vis gruppefarger</span></label>)}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-3">Tekststil</h3>
                                        <div className="space-y-2">
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={textStyle.bold} onChange={(e) => setTextStyle(prev => ({ ...prev, bold: e.target.checked }))} className="rounded" /><span className="text-sm">Fet skrift</span></label>
                                            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={textStyle.italic} onChange={(e) => setTextStyle(prev => ({ ...prev, italic: e.target.checked }))} className="rounded" /><span className="text-sm">Kursiv skrift</span></label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg border">
                                <h3 className="text-lg font-medium text-gray-900 mb-3">Fargeinnstillinger</h3>
                                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                                    {[
                                        { label: 'Gutt', category: 'gender', keyName: 'gutt' },
                                        { label: 'Jente', category: 'gender', keyName: 'jente' },
                                        { label: 'Annet', category: 'gender', keyName: 'annet' },
                                        { label: groups.A, category: 'group', keyName: 'A' },
                                        { label: groups.B, category: 'group', keyName: 'B' },
                                    ].map(item => (
                                        <div key={item.label} className="flex items-center gap-2">
                                            <input type="color" id={`color-${item.keyName}`} className="absolute opacity-0 w-0 h-0" value={colorSettings[item.category][item.keyName]} onChange={(e) => setColorSettings(prev => ({ ...prev, [item.category]: { ...prev[item.category], [item.keyName]: e.target.value } }))} />
                                            <label htmlFor={`color-${item.keyName}`} className="w-5 h-5 rounded-full border border-gray-300 cursor-pointer" style={{ backgroundColor: colorSettings[item.category][item.keyName] }}></label>
                                            <span className="text-sm text-gray-600">{item.label}</span>
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-2">
                                         <input type="color" id="color-frame" className="absolute opacity-0 w-0 h-0" value={colorSettings.frame} onChange={(e) => setColorSettings(prev => ({ ...prev, frame: e.target.value }))} />
                                         <label htmlFor="color-frame" className="w-5 h-5 rounded-full border border-gray-300 cursor-pointer" style={{ backgroundColor: colorSettings.frame }}></label>
                                         <span className="text-sm text-gray-600">Ramme</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                         <input type="color" id="color-podium" className="absolute opacity-0 w-0 h-0" value={colorSettings.podium} onChange={(e) => setColorSettings(prev => ({ ...prev, podium: e.target.value }))} />
                                         <label htmlFor="color-podium" className="w-5 h-5 rounded-full border border-gray-300 cursor-pointer" style={{ backgroundColor: colorSettings.podium }}></label>
                                         <span className="text-sm text-gray-600">Kateter</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                         <input type="color" id="color-text" className="absolute opacity-0 w-0 h-0" value={colorSettings.textColor} onChange={(e) => setColorSettings(prev => ({ ...prev, textColor: e.target.value }))} />
                                         <label htmlFor="color-text" className="w-5 h-5 rounded-full border border-gray-300 cursor-pointer" style={{ backgroundColor: colorSettings.textColor }}></label>
                                         <span className="text-sm text-gray-600">Tekst</span>
                                    </div>
                                </div>
                            </div>

                             {desks.length === 0 ? (
                                 <div className="text-center py-12 text-gray-500"><p>Ingen pulter er satt opp ennå.</p><p>Gå til "Klasserom"-fanen for å designe klasserommet.</p></div>
                             ) : (
                                 <>
                                     <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                                         <div style={{ position: 'relative', width: '100%', maxWidth: '800px', aspectRatio: '800 / 533', margin: '0 auto', border: '2px solid #374151', borderRadius: '8px', overflow: 'hidden' }}>
                                             <canvas ref={seatingCanvasRef} width={canvasSize.width} height={canvasSize.height} onClick={handleSeatingChartClick} className="w-full h-full block cursor-pointer" style={{ imageRendering: 'pixelated' }} />
                                         </div>
                                     </div>
                                     <div className="bg-gray-50 p-4 rounded-lg">
                                         <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                             <div><div className="text-2xl font-bold text-blue-600">{desks.length}</div><div className="text-sm text-gray-600">Pulter</div></div>
                                             <div><div className="text-2xl font-bold text-green-600">{desks.reduce((sum, desk) => sum + desk.capacity, 0)}</div><div className="text-sm text-gray-600">Plasser totalt</div></div>
                                             <div><div className="text-2xl font-bold text-purple-600">{students.length}</div><div className="text-sm text-gray-600">Elever</div></div>
                                             <div><div className="text-2xl font-bold text-orange-600">{Object.values(seatingArrangement).reduce((sum, deskStudents) => sum + deskStudents.length, 0)}</div><div className="text-sm text-gray-600">Plasserte elever</div></div>
                                         </div>
                                     </div>
                                 </>
                             )}
                         </div>
                    )}
                </div>
            </main>
        </div>
         </> // <--- LEGG TIL DENNE LINJEN
    );
};

export default ClassroomDesigner;